import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  Autocomplete,
  CircularProgress,
  Skeleton,
  Avatar,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Delete, Visibility, VisibilityOff, ContentCopy, AddOutlined, Refresh,
  Edit as EditIcon, PersonAdd, PersonRemoveOutlined, CancelOutlined,
} from '@mui/icons-material';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { App } from '../types';

function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), ms); }) as T;
}

interface EndpointMeta { key: string; description: string; }
// null = not set (app's daily limit applies); 0 = model blocked.
interface ModelLimit { input_tokens: number | null; output_tokens: number | null; }
interface EndpointConfig {
  enabled: boolean;
  models: string[];
  model_limits: Record<string, ModelLimit>;
}
type EndpointPermissions = Record<string, EndpointConfig>;
const fmtModelCap = (n: number | null | undefined, fmt: (v: number) => string) =>
  n === null || n === undefined ? '' : fmt(n);

// ---------------------------------------------------------------------------
// Weekly/monthly caps stack on top of the daily budget — a weekly cap tighter
// than the daily limit would trip before a single day even elapses, and a
// monthly cap tighter than either would trip before a single week does.
// Blank stays blank: caps are optional, and this only fires once one is set.
// ---------------------------------------------------------------------------
const validateCapHierarchy = (
  dailyInput: number,
  dailyOutput: number,
  weeklyInputRaw: string,
  weeklyOutputRaw: string,
  monthlyInputRaw: string,
  monthlyOutputRaw: string,
): string | null => {
  const weeklyInput = weeklyInputRaw === '' ? null : parseInt(weeklyInputRaw, 10);
  const weeklyOutput = weeklyOutputRaw === '' ? null : parseInt(weeklyOutputRaw, 10);
  const monthlyInput = monthlyInputRaw === '' ? null : parseInt(monthlyInputRaw, 10);
  const monthlyOutput = monthlyOutputRaw === '' ? null : parseInt(monthlyOutputRaw, 10);

  if (weeklyInput !== null && weeklyInput < dailyInput) {
    return 'Weekly Input Cap can\'t be less than the Daily Input Token Limit.';
  }
  if (weeklyOutput !== null && weeklyOutput < dailyOutput) {
    return 'Weekly Output Cap can\'t be less than the Daily Output Token Limit.';
  }
  if (monthlyInput !== null) {
    const floor = weeklyInput !== null ? Math.max(dailyInput, weeklyInput) : dailyInput;
    if (monthlyInput < floor) {
      return weeklyInput !== null
        ? 'Monthly Input Cap can\'t be less than the Daily or Weekly Input limits.'
        : 'Monthly Input Cap can\'t be less than the Daily Input Token Limit.';
    }
  }
  if (monthlyOutput !== null) {
    const floor = weeklyOutput !== null ? Math.max(dailyOutput, weeklyOutput) : dailyOutput;
    if (monthlyOutput < floor) {
      return weeklyOutput !== null
        ? 'Monthly Output Cap can\'t be less than the Daily or Weekly Output limits.'
        : 'Monthly Output Cap can\'t be less than the Daily Output Token Limit.';
    }
  }
  return null;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const AppsPage: React.FC = () => {
  const { user } = useAuth();
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { appId: urlAppId } = useParams<{ appId: string }>();
  const isEditUrl = urlAppId && location.pathname.endsWith('/edit');
  const autoOpenedRef = useRef<string | null>(null);
  const isDark = mode === 'dark';

  const textP   = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS   = isDark ? '#888888' : '#666666';
  const textM   = isDark ? '#555555' : '#AAAAAA';
  const card    = isDark ? '#111111' : '#FFFFFF';
  const border  = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const subBg   = isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC';
  const sectionLabel = { fontSize: '0.7rem', color: textM, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' };

  // ── Core state ────────────────────────────────────────────────────────────
  const [apps, setApps]             = useState<App[]>([]);
  const [productionOnly, setProductionOnly] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [registering, setRegistering]   = useState(false);
  const [saving, setSaving]             = useState(false);
  const [openCreate, setOpenCreate]     = useState(false);
  const [newAuthKey, setNewAuthKey]     = useState<string | null>(null);
  const [showAuthKey, setShowAuthKey]   = useState(false);
  const [viewedAuthKey, setViewedAuthKey]   = useState<string | null>(null);
  const [showViewedAuthKey, setShowViewedAuthKey] = useState(false);
  const [fetchingAuthKey, setFetchingAuthKey]     = useState(false);
  const [selectedApp, setSelectedApp]   = useState<App | null>(null);
  const [openDetails, setOpenDetails]   = useState(false);
  const [appActivity, setAppActivity]   = useState<import('../types').AdminLog[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [openEdit, setOpenEdit]         = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>({
    open: false, msg: '', severity: 'success',
  });

  // ── Viewer state ──────────────────────────────────────────────────────────
  const [viewerEmail, setViewerEmail]   = useState('');
  const [viewerDirOptions, setViewerDirOptions] = useState<{ name: string; email: string; photo?: string | null }[]>([]);
  const [viewerDirLoading, setViewerDirLoading] = useState(false);
  const [viewerDirValue, setViewerDirValue] = useState<{ name: string; email: string; photo?: string | null } | null>(null);
  const [addingViewer, setAddingViewer]   = useState(false);
  const [removingViewer, setRemovingViewer] = useState<string | null>(null);

  // ── Register viewers ──────────────────────────────────────────────────────
  const [registerViewers, setRegisterViewers]         = useState<string[]>([]);
  const [regViewerDirOptions, setRegViewerDirOptions] = useState<{ name: string; email: string; photo?: string | null }[]>([]);
  const [regViewerDirLoading, setRegViewerDirLoading] = useState(false);
  const [regViewerDirValue, setRegViewerDirValue]     = useState<{ name: string; email: string; photo?: string | null } | null>(null);

  // ── Register form ─────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    app_name: '', department: '', project_head: '', openai_api_key: '', anthropic_api_key: '',
    token_limit_input: 1000000, token_limit_output: 50000, is_production: false,
    weekly_token_limit_input: '' as string, weekly_token_limit_output: '' as string,
    monthly_token_limit_input: '' as string, monthly_token_limit_output: '' as string,
  });
  const [dirOptions, setDirOptions]   = useState<{ name: string; email: string }[]>([]);
  const [dirLoading, setDirLoading]   = useState(false);

  // ── Register endpoint/model state ─────────────────────────────────────────
  const [endpointCatalog, setEndpointCatalog]     = useState<EndpointMeta[]>([]);
  const [endpointPerms, setEndpointPerms]         = useState<EndpointPermissions>({});
  const [availableModels, setAvailableModels]     = useState<string[]>([]);
  const [classifiedModels, setClassifiedModels]   = useState<Record<string, string[]>>({});
  const [fetchingModels, setFetchingModels]       = useState(false);
  const [modelsFetched, setModelsFetched]         = useState(false);
  const [confirmFetchOpen, setConfirmFetchOpen]   = useState(false);

  // ── Edit dialog state ─────────────────────────────────────────────────────
  const [editDirOptions, setEditDirOptions]   = useState<{ name: string; email: string }[]>([]);
  const [editDirLoading, setEditDirLoading]   = useState(false);
  const [editForm, setEditForm] = useState({
    department: '', project_head: '',
    token_limit_input: 1000000, token_limit_output: 50000, is_active: true, is_production: false,
    weekly_token_limit_input: '' as string, weekly_token_limit_output: '' as string,
    monthly_token_limit_input: '' as string, monthly_token_limit_output: '' as string,
  });
  const [editEndpointPerms, setEditEndpointPerms] = useState<EndpointPermissions>({});

  // ── Edit dialog: model fetching ───────────────────────────────────────────
  const [editAvailableModels, setEditAvailableModels]   = useState<string[]>([]);
  const [editClassifiedModels, setEditClassifiedModels] = useState<Record<string, string[]>>({});
  const [editFetchingModels, setEditFetchingModels]     = useState(false);
  const [editModelsFetched, setEditModelsFetched]       = useState(false);

  // ── Update API key state ──────────────────────────────────────────────────
  const [newOpenAIKey, setNewOpenAIKey]     = useState('');
  const [newAnthropicKey, setNewAnthropicKey] = useState('');
  const [updatingKeys, setUpdatingKeys]     = useState(false);
  const [showUpdateKeys, setShowUpdateKeys] = useState(false);
  const [removalTarget, setRemovalTarget] = useState<'openai' | 'anthropic' | null>(null);
  const [confirmRemoveKey, setConfirmRemoveKey] = useState(false);
  const [retryingCreditChecks, setRetryingCreditChecks] = useState(false);
  const [retryingAllCreditChecks, setRetryingAllCreditChecks] = useState(false);

  // ── Edit dialog: provider key state ───────────────────────────────────────
  const [editOpenAIKey, setEditOpenAIKey]       = useState('');
  const [editAnthropicKey, setEditAnthropicKey] = useState('');
  const [showEditProviders, setShowEditProviders] = useState(false);
  const [removeAnthropicKey, setRemoveAnthropicKey] = useState(false);

  // ── Request dialogs ───────────────────────────────────────────────────────
  const [reqTokenOpen, setReqTokenOpen]     = useState(false);
  const [reqModelOpen, setReqModelOpen]     = useState(false);
  const [reqReason, setReqReason]           = useState('');
  const [reqInputLimit, setReqInputLimit]   = useState('');
  const [reqOutputLimit, setReqOutputLimit] = useState('');
  const [reqModels, setReqModels]           = useState('');
  const [submittingReq, setSubmittingReq]   = useState(false);

  // ── Lifetime usage state ──────────────────────────────────────────────────
  const [lifetimeUsage, setLifetimeUsage]   = useState<any>(null);
  const [lifetimeLoading, setLifetimeLoading] = useState(false);

  // ── Directory search callbacks ────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchDir = useCallback(debounce(async (q: string) => {
    if (q.length < 2) return;
    setDirLoading(true);
    try { setDirOptions(await apiService.searchDirectory(q)); }
    catch { setDirOptions([]); } finally { setDirLoading(false); }
  }, 400), []);

  // ── Endpoint-Provider mapping ─────────────────────────────────────────────
  // Determine which providers support each endpoint
  const getEndpointProviders = (
    endpointKey: string, 
    models: string[],
    hasOpenAIKey: boolean,
    hasAnthropicKey: boolean
  ): string[] => {
    const endpointModels = models || [];
    const hasOpenAI = endpointModels.some(m => 
      m.startsWith('gpt-') || m.startsWith('o1-') || m.startsWith('o3-') || 
      m.startsWith('chatgpt-') || m.includes('whisper') || m.includes('dall-e') || 
      m.includes('tts') || m.includes('embed') || m.includes('davinci') || 
      m.includes('instruct')
    );
    const hasAnthropic = endpointModels.some(m => m.startsWith('claude-'));
    
    const providers: string[] = [];
    if (hasOpenAI) providers.push('openai');
    if (hasAnthropic) providers.push('anthropic');
    
    // If no models, infer from endpoint name (for endpoints with empty model_patterns)
    if (providers.length === 0) {
      // Multi-provider endpoints
      if (endpointKey === 'chat/completions' || endpointKey === 'responses') {
        if (hasOpenAIKey) providers.push('openai');
        if (hasAnthropicKey) providers.push('anthropic');
      } else {
        // All other endpoints are OpenAI-only
        if (hasOpenAIKey) providers.push('openai');
      }
    }
    
    return providers;
  };

  // Filter endpoints based on configured providers
  const filterEndpointsByProviders = (
    catalog: EndpointMeta[],
    classifiedModels: Record<string, string[]>,
    hasOpenAI: boolean,
    hasAnthropic: boolean
  ): EndpointMeta[] => {
    if (!hasOpenAI && !hasAnthropic) return [];
    if (hasOpenAI && hasAnthropic) return catalog; // Show all if both configured
    
    return catalog.filter(entry => {
      const models = classifiedModels[entry.key] || [];
      const providers = getEndpointProviders(entry.key, models, hasOpenAI, hasAnthropic);
      
      if (hasOpenAI && !hasAnthropic) {
        // Only OpenAI configured: show OpenAI-only and multi-provider endpoints
        return providers.includes('openai');
      }
      if (hasAnthropic && !hasOpenAI) {
        // Only Anthropic configured: show multi-provider endpoints only
        return providers.includes('anthropic');
      }
      
      return true;
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchEditDir = useCallback(debounce(async (q: string) => {
    if (q.length < 2) return;
    setEditDirLoading(true);
    try { setEditDirOptions(await apiService.searchDirectory(q)); }
    catch { setEditDirOptions([]); } finally { setEditDirLoading(false); }
  }, 400), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchViewerDir = useCallback(debounce(async (q: string) => {
    if (q.length < 2) return;
    setViewerDirLoading(true);
    try { setViewerDirOptions(await apiService.searchDirectory(q)); }
    catch { setViewerDirOptions([]); } finally { setViewerDirLoading(false); }
  }, 400), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchRegViewerDir = useCallback(debounce(async (q: string) => {
    if (q.length < 2) return;
    setRegViewerDirLoading(true);
    try { setRegViewerDirOptions(await apiService.searchDirectory(q)); }
    catch { setRegViewerDirOptions([]); } finally { setRegViewerDirLoading(false); }
  }, 400), []);

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadApps(); loadCatalog(); }, []);

  // ── Auto-open dialog when URL contains an appId (detail or edit) ─────────
  useEffect(() => {
    if (!urlAppId || apps.length === 0) return;
    const key = urlAppId + (isEditUrl ? '/edit' : '');
    if (autoOpenedRef.current === key) return;
    const target = apps.find(a => a.app_id === urlAppId);
    if (target) {
      autoOpenedRef.current = key;
      if (isEditUrl) openEditDialog(target);
      else openDetailsDialog(target);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlAppId, isEditUrl, apps]);

  const loadCatalog = async () => {
    try {
      const catalog = await apiService.getEndpointCatalog();
      setEndpointCatalog(catalog);
      const initial: EndpointPermissions = {};
      catalog.forEach(({ key }) => { initial[key] = { enabled: false, models: [], model_limits: {} }; });
      setEndpointPerms(initial);
    } catch { /* catalog stays empty */ }
  };

  const loadApps = async () => {
    setTableLoading(true);
    try {
      const data = await apiService.listApps();
      setApps(Array.isArray(data) ? data : (data?.apps || []));
    } catch { showSnack('Failed to load applications', 'error'); }
    finally { setTableLoading(false); }
  };

  const showSnack = (msg: string, severity: 'success' | 'error' | 'info' = 'success') =>
    setSnack({ open: true, msg, severity });

  // ── Viewer management ─────────────────────────────────────────────────────
  const handleAddViewer = async () => {
    if (!selectedApp || !viewerEmail) return;
    setAddingViewer(true);
    try {
      await apiService.addAppViewer(selectedApp.app_id, viewerEmail);
      showSnack(`${viewerEmail} added as viewer`, 'success');
      setViewerEmail(''); setViewerDirValue(null); setViewerDirOptions([]);
      loadApps();
      setSelectedApp(prev => prev ? { ...prev, viewers: [...(prev.viewers ?? []), viewerEmail.toLowerCase()] } : prev);
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Failed to add viewer', 'error'); }
    finally { setAddingViewer(false); }
  };

  const handleRemoveViewer = async (email: string) => {
    if (!selectedApp) return;
    setRemovingViewer(email);
    try {
      await apiService.removeAppViewer(selectedApp.app_id, email);
      showSnack(`${email} removed from viewers`, 'success');
      setSelectedApp(prev => prev ? { ...prev, viewers: (prev.viewers ?? []).filter(v => v !== email) } : prev);
      loadApps();
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Failed to remove viewer', 'error'); }
    finally { setRemovingViewer(null); }
  };

  // ── Register: fetch models ─────────────────────────────────────────────────
  const handleFetchModelsClick = () => {
     if (!formData.openai_api_key && !formData.anthropic_api_key) return;
    setConfirmFetchOpen(true);
  };

  const handleConfirmFetchModels = async () => {
    if (!formData.openai_api_key && !formData.anthropic_api_key) return;
    setConfirmFetchOpen(false);
    setFetchingModels(true);
    try {
      const { models, endpoint_models, providers } = await apiService.fetchModels(
        formData.openai_api_key || undefined,
        formData.anthropic_api_key || undefined
      );
      setAvailableModels(models);
      setClassifiedModels(endpoint_models);
      setModelsFetched(true);
      const providerStr = providers.join(' + ') || 'providers';
      showSnack(`${models.length} models fetched from ${providerStr}`, 'info');
    } catch (e: any) {
      const errorMsg = e.response?.data?.detail || 'Failed to fetch models';
      showSnack(errorMsg, 'error');
    } finally { setFetchingModels(false); }
  };

  // ── Edit: fetch models via stored key ─────────────────────────────────────
  const handleFetchEditModels = async () => {
    if (!selectedApp) return;
    setEditFetchingModels(true);
    try {
      const { models, endpoint_models, providers } = await apiService.fetchModelsForApp(selectedApp.app_id);
      setEditAvailableModels(models);
      setEditClassifiedModels(endpoint_models);
      setEditModelsFetched(true);
      const providerStr = providers.join(' + ') || 'providers';
      showSnack(`${models.length} models loaded from ${providerStr}`, 'info');
    } catch (e: any) {
      const errorMsg = e.response?.data?.detail || 'Failed to load models';
      showSnack(errorMsg, 'error');
    } finally { setEditFetchingModels(false); }
  };

  // ── Register ──────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (formData.app_name.trim().toLowerCase() === 'admin') {
      showSnack("'admin' is a reserved application name. Please choose a different name.", 'error');
      return;
    }
    const capError = validateCapHierarchy(
      formData.token_limit_input, formData.token_limit_output,
      formData.weekly_token_limit_input, formData.weekly_token_limit_output,
      formData.monthly_token_limit_input, formData.monthly_token_limit_output,
    );
    if (capError) { showSnack(capError, 'error'); return; }
    const permissions: Record<string, any> = {};
    for (const [key, cfg] of Object.entries(endpointPerms)) {
      if (cfg.enabled) {
        permissions[key] = {
          allowed_models: cfg.models.length > 0 ? cfg.models : ['*'],
          model_limits: cfg.model_limits,
        };
      }
    }
    const payload: any = { ...formData };
    // Weekly / monthly caps are optional — empty string = unset, don't send.
    for (const capField of ['weekly_token_limit_input', 'weekly_token_limit_output', 'monthly_token_limit_input', 'monthly_token_limit_output'] as const) {
      const raw = (formData as any)[capField];
      if (raw === '' || raw === null || raw === undefined) {
        delete payload[capField];
      } else {
        const n = parseInt(String(raw), 10);
        payload[capField] = Number.isFinite(n) && n > 0 ? n : undefined;
      }
    }
    if (Object.keys(permissions).length > 0) payload.permissions = permissions;
    if (registerViewers.length > 0) payload.viewers = registerViewers;
    setRegistering(true);
    try {
      const res = await apiService.registerApp(payload);
      setOpenCreate(false); resetForm();
      if (res?.auth_key) { setNewAuthKey(res.auth_key); setShowAuthKey(false); }
      loadApps();
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Failed to register application', 'error');
    } finally { setRegistering(false); }
  };

  const resetForm = () => {
    setFormData({
      app_name: '', department: '', project_head: '', openai_api_key: '', anthropic_api_key: '',
      token_limit_input: 1000000, token_limit_output: 50000, is_production: false,
      weekly_token_limit_input: '', weekly_token_limit_output: '',
      monthly_token_limit_input: '', monthly_token_limit_output: '',
    });
    setAvailableModels([]); setClassifiedModels({}); setModelsFetched(false); setDirOptions([]);
    setRegisterViewers([]); setRegViewerDirValue(null); setRegViewerDirOptions([]);
    const reset: EndpointPermissions = {};
    endpointCatalog.forEach(({ key }) => { reset[key] = { enabled: false, models: [], model_limits: {} }; });
    setEndpointPerms(reset);
  };

  // ── Delete — requires 2 super admin approvals ─────────────────────────────
  const handleDelete = async (app: App) => {
    const message =
      `Initiate deletion of "${app.app_name}"?\n\n` +
      `⚠  API traffic to this app will be frozen (423 Locked) the moment you initiate this ` +
      `deletion, and stays frozen until a second super admin approves or cancels the request.`;
    if (!window.confirm(message)) return;
    try {
      const res = await apiService.initiateAppDeletion(app.app_id);
      showSnack(res.message, 'info');
      loadApps();
      if (selectedApp?.app_id === app.app_id) setSelectedApp(prev => prev ? { ...prev, pending_action: 'delete', deletion_pending: true } : prev);
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Failed to initiate deletion', 'error'); }
  };

  const handleCancelDeletion = async (app: App) => {
    if (!window.confirm(`Cancel the pending deletion of "${app.app_name}"?`)) return;
    try {
      const res = await apiService.cancelAppDeletion(app.app_id);
      showSnack(res.message || 'Deletion cancelled', 'success');
      loadApps();
      if (selectedApp?.app_id === app.app_id) setSelectedApp(prev => prev ? { ...prev, deletion_pending: undefined, pending_action: undefined } : prev);
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Failed to cancel deletion', 'error'); }
  };

  // ── Rename — requires 2 super admin approvals ─────────────────────────────
  const [openRename, setOpenRename] = useState(false);
  const [renameTarget, setRenameTarget] = useState<App | null>(null);
  const [renameNewName, setRenameNewName] = useState('');
  const [renaming, setRenaming] = useState(false);

  const openRenameDialog = (app: App) => {
    setRenameTarget(app);
    setRenameNewName(app.app_name);
    setOpenRename(true);
  };

  const closeRenameDialog = () => {
    if (renaming) return;
    setOpenRename(false);
    setRenameTarget(null);
    setRenameNewName('');
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    const trimmed = renameNewName.trim();
    if (!trimmed) { showSnack('New name cannot be empty', 'error'); return; }
    if (trimmed === renameTarget.app_name) { showSnack('New name must differ from the current name', 'error'); return; }
    if (trimmed.toLowerCase() === 'admin') { showSnack("'admin' is a reserved application name.", 'error'); return; }
    setRenaming(true);
    try {
      const res = await apiService.initiateAppRename(renameTarget.app_id, trimmed);
      showSnack(res.message || 'Rename initiated', 'info');
      const targetId = renameTarget.app_id;
      setOpenRename(false);
      setRenameTarget(null);
      setRenameNewName('');
      loadApps();
      if (selectedApp?.app_id === targetId) {
        setSelectedApp(prev => prev ? { ...prev, pending_action: 'rename', pending_rename_new_name: trimmed } : prev);
      }
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Failed to initiate rename', 'error');
    } finally {
      setRenaming(false);
    }
  };

  const handleCancelRename = async (app: App) => {
    if (!window.confirm(`Cancel the pending rename of "${app.app_name}"?`)) return;
    try {
      const res = await apiService.cancelAppRename(app.app_id);
      showSnack(res.message || 'Rename cancelled', 'success');
      loadApps();
      if (selectedApp?.app_id === app.app_id) {
        setSelectedApp(prev => prev ? { ...prev, pending_action: undefined, pending_rename_new_name: undefined } : prev);
      }
    } catch (e: any) { showSnack(e.response?.data?.detail || 'Failed to cancel rename', 'error'); }
  };

  // ── Open edit dialog ──────────────────────────────────────────────────────
  const openEditDialog = (app: App) => {
    setSelectedApp(app);
    setEditForm({
      department: app.department || '', project_head: app.project_head || '',
      token_limit_input: app.token_limit_input, token_limit_output: app.token_limit_output,
      is_active: app.is_active, is_production: !!app.is_production,
      weekly_token_limit_input: app.weekly_token_limit_input != null ? String(app.weekly_token_limit_input) : '',
      weekly_token_limit_output: app.weekly_token_limit_output != null ? String(app.weekly_token_limit_output) : '',
      monthly_token_limit_input: app.monthly_token_limit_input != null ? String(app.monthly_token_limit_input) : '',
      monthly_token_limit_output: app.monthly_token_limit_output != null ? String(app.monthly_token_limit_output) : '',
    });
    const perms: EndpointPermissions = {};
    endpointCatalog.forEach(({ key }) => {
      const ex = app.permissions?.[key];
      perms[key] = ex
        ? {
            enabled: true,
            models: ex.allowed_models ?? [],
            model_limits: ex.model_limits
              ? Object.fromEntries(Object.entries(ex.model_limits).map(([m, l]: [string, any]) => [m, { input_tokens: l.input_tokens, output_tokens: l.output_tokens }]))
              : {},
          }
        : { enabled: false, models: [], model_limits: {} };
    });
    if (app.permissions) {
      Object.entries(app.permissions).forEach(([key, perm]) => {
        if (!perms[key]) perms[key] = { enabled: true, models: perm.allowed_models ?? [], model_limits: perm.model_limits ?? {} };
      });
    }
    setEditEndpointPerms(perms);
    setEditDirOptions([]);
    setViewerEmail(''); setViewerDirValue(null); setViewerDirOptions([]);
    // Reset model fetch state for this app
    setEditAvailableModels([]); setEditClassifiedModels({});
    setEditModelsFetched(false); setEditFetchingModels(false);
    // Reset provider key state
    setEditOpenAIKey(''); setEditAnthropicKey(''); setShowEditProviders(false); setRemoveAnthropicKey(false);
    setOpenEdit(true);
    navigate('/apps/' + app.app_id + '/edit', { replace: true });
  };

  const closeEditDialog = () => {
    setOpenEdit(false);
    autoOpenedRef.current = null;
    setTimeout(() => navigate('/apps', { replace: true }), 250);
  };

  // ── Save edit ─────────────────────────────────────────────────────────────
  const handleUpdate = async () => {
    if (!selectedApp) return;
    const capError = validateCapHierarchy(
      editForm.token_limit_input, editForm.token_limit_output,
      editForm.weekly_token_limit_input, editForm.weekly_token_limit_output,
      editForm.monthly_token_limit_input, editForm.monthly_token_limit_output,
    );
    if (capError) { showSnack(capError, 'error'); return; }
    const payload: Record<string, any> = {};
    if (editForm.department !== selectedApp.department) payload.department = editForm.department;
    if (editForm.project_head !== selectedApp.project_head) payload.project_head = editForm.project_head;
    if (editForm.token_limit_input !== selectedApp.token_limit_input) payload.token_limit_input = editForm.token_limit_input;
    if (editForm.token_limit_output !== selectedApp.token_limit_output) payload.token_limit_output = editForm.token_limit_output;
    if (editForm.is_active !== selectedApp.is_active) payload.is_active = editForm.is_active;
    if (editForm.is_production !== !!selectedApp.is_production) payload.is_production = editForm.is_production;
    // Weekly/monthly caps: send 0 to clear (backend normalises 0 → null), a positive int to set,
    // or omit entirely when the field value hasn't changed from what the app carries.
    for (const capField of ['weekly_token_limit_input', 'weekly_token_limit_output', 'monthly_token_limit_input', 'monthly_token_limit_output'] as const) {
      const raw = (editForm as any)[capField] as string;
      const existing = (selectedApp as any)[capField] ?? null;
      const parsed = raw === '' ? null : parseInt(raw, 10);
      if (parsed === existing) continue;
      // Send 0 to explicitly clear (backend treats <=0 as unset).
      payload[capField] = parsed === null ? 0 : parsed;
    }

    const permissions: Record<string, any> = {};
    for (const [key, cfg] of Object.entries(editEndpointPerms)) {
      if (cfg.enabled) {
        permissions[key] = {
          allowed_models: cfg.models.length > 0 ? cfg.models : ['*'],
          model_limits: cfg.model_limits || {},
        };
      }
    }
    payload.permissions = permissions;

    setSaving(true);
    try {
      // Update provider keys if provided or if removal is requested
      if (editOpenAIKey.trim() || editAnthropicKey.trim() || removeAnthropicKey) {
        const keys: { openai_api_key?: string; anthropic_api_key?: string; remove_anthropic_key?: boolean } = {};
        if (editOpenAIKey.trim()) keys.openai_api_key = editOpenAIKey.trim();
        if (editAnthropicKey.trim()) keys.anthropic_api_key = editAnthropicKey.trim();
        if (removeAnthropicKey) keys.remove_anthropic_key = true;
        await apiService.updateAppKeys(selectedApp.app_id, keys);
      }
      await apiService.updateApp(selectedApp.app_id, payload);
      closeEditDialog();
      showSnack('Application updated successfully');
      loadApps();
    } catch (e: any) {
      showSnack(e.response?.data?.detail || 'Failed to update application', 'error');
    } finally { setSaving(false); }
  };

  // ── Open details dialog ───────────────────────────────────────────────────
  const closeDetailsDialog = () => {
    setOpenDetails(false);
    setViewedAuthKey(null);
    setShowViewedAuthKey(false);
    autoOpenedRef.current = null;
    setTimeout(() => navigate('/apps', { replace: true }), 250);
  };

  const openDetailsDialog = async (app: App) => {
    setSelectedApp(app);
    setLifetimeUsage(null);
    setAppActivity([]);
    setOpenDetails(true);
    navigate('/apps/' + app.app_id, { replace: true });
    // Load lifetime usage + activity in background
    setLifetimeLoading(true);
    setActivityLoading(true);
    await Promise.allSettled([
      apiService.getAppLifetimeUsage(app.app_id).then(setLifetimeUsage).finally(() => setLifetimeLoading(false)),
      apiService.getAppActivity(app.app_id).then(d => {
      const READ_ONLY = new Set(['KEYS_APP', 'USAGE_APP', 'LIFETIME_USAGE', 'ADMIN_LOGIN']);
      setAppActivity((d.logs || []).filter((l: import('../types').AdminLog) => !READ_ONLY.has(l.action)));
    }).finally(() => setActivityLoading(false)),
    ]);
  };

  // ── Misc ──────────────────────────────────────────────────────────────────
  const copyToClipboard = (text: string, label = 'Copied') => {
    navigator.clipboard.writeText(text);
    showSnack(`${label} copied to clipboard`, 'info');
  };

  // Endpoint perm setters — Register
  const toggleEndpoint    = (key: string, v: boolean)  => setEndpointPerms(p => ({ ...p, [key]: { ...p[key], enabled: v } }));
  const setEndpointModels = (key: string, v: string[]) => setEndpointPerms(p => ({ ...p, [key]: { ...p[key], models: v } }));
  const setEndpointModelLimit = (key: string, model: string, field: keyof ModelLimit, val: number | null) =>
    setEndpointPerms(p => ({ ...p, [key]: { ...p[key], model_limits: { ...p[key].model_limits, [model]: { ...(p[key].model_limits[model] ?? { input_tokens: null, output_tokens: null }), [field]: val } } } }));

  // Endpoint perm setters — Edit
  const toggleEditEndpoint    = (key: string, v: boolean)  => setEditEndpointPerms(p => ({ ...p, [key]: { ...p[key], enabled: v } }));
  const setEditEndpointModels = (key: string, v: string[]) => setEditEndpointPerms(p => ({ ...p, [key]: { ...p[key], models: v } }));
  const setEditEndpointModelLimit = (key: string, model: string, field: keyof ModelLimit, val: number | null) =>
    setEditEndpointPerms(p => ({ ...p, [key]: { ...p[key], model_limits: { ...p[key].model_limits, [model]: { ...(p[key].model_limits[model] ?? { input_tokens: null, output_tokens: null }), [field]: val } } } }));

  const anyEndpointEnabled = Object.values(endpointPerms).some(c => c.enabled);

  // ── Endpoint row renderer ─────────────────────────────────────────────────
  const renderEndpointRow = (
    catalogEntry: EndpointMeta,
    cfg: EndpointConfig,
    models: string[],
    onToggle: (key: string, v: boolean) => void,
    onModelsChange: (key: string, v: string[]) => void,
    onLimitChange?: (key: string, model: string, field: keyof ModelLimit, val: number | null) => void,
    hasOpenAIKey?: boolean,
    hasAnthropicKey?: boolean,
  ) => {
    const { key, description } = catalogEntry;
    const namedModels = cfg.models.filter(m => m !== '*');
    
    // Determine providers for this endpoint
    const providers = getEndpointProviders(key, models, hasOpenAIKey ?? false, hasAnthropicKey ?? false);

    return (
      <Box key={key} sx={{
        border: '1px solid',
        borderColor: cfg.enabled ? '#FF1A50' : border,
        borderRadius: '6px', overflow: 'hidden',
        transition: 'border-color 0.15s',
      }}>
        {/* Header row */}
        <Box sx={{
          px: 1.5, py: 0.75,
          bgcolor: cfg.enabled
            ? (isDark ? 'rgba(255,26,80,0.08)' : 'rgba(255,26,80,0.04)')
            : subBg,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Box>
              <code style={{ fontSize: '0.8rem', fontWeight: 600, color: textP, background: 'none', padding: 0 }}>/{key}</code>
              <Typography sx={{ fontSize: '0.7rem', color: textM, display: 'inline', ml: 1 }}>{description}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
              {providers.includes('openai') && (
                <Chip 
                  label="OpenAI" 
                  size="small" 
                  sx={{ 
                    bgcolor: isDark ? 'rgba(16,185,129,0.12)' : '#d1fae5',
                    color: '#10B981',
                    fontWeight: 600, 
                    fontSize: '0.55rem', 
                    height: 14,
                    border: 'none',
                    '& .MuiChip-label': { px: 0.5 }
                  }} 
                />
              )}
              {providers.includes('anthropic') && (
                <Chip 
                  label="Anthropic" 
                  size="small" 
                  sx={{ 
                    bgcolor: isDark ? 'rgba(168,85,247,0.12)' : '#f3e8ff',
                    color: '#A855F7',
                    fontWeight: 600, 
                    fontSize: '0.55rem', 
                    height: 14,
                    border: 'none',
                    '& .MuiChip-label': { px: 0.5 }
                  }} 
                />
              )}
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Switch size="small" checked={cfg.enabled} onChange={(e) => onToggle(key, e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF1A50' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#FF1A50' },
                }}
              />
            }
            label="" sx={{ m: 0 }}
          />
        </Box>

        {cfg.enabled && (
          <Box sx={{ bgcolor: card, borderTop: `1px solid ${border}` }}>
            {/* Model autocomplete */}
            <Box sx={{ px: 1.5, py: 1 }}>
              <Autocomplete<string, true, false, true>
                multiple freeSolo size="small"
                options={models}
                value={cfg.models}
                onChange={(_, newVal) => onModelsChange(key, newVal as string[])}
                renderValue={(value, getItemProps) =>
                  (value as string[]).map((option, index) => (
                    <Chip
                      label={option === '*' ? '* (all)' : option}
                      size="small"
                      {...getItemProps({ index })}
                      key={option}
                      sx={{
                        bgcolor: option === '*'
                          ? (isDark ? 'rgba(255,26,80,0.15)' : '#fee2e2')
                          : (isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'),
                        color: option === '*' ? '#FF1A50' : textS,
                        fontSize: '0.7rem', height: 20, border: 'none',
                        fontWeight: option === '*' ? 600 : 400,
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      models.length === 0
                        ? cfg.models.length === 0 ? 'Load models first or type to add manually' : 'Add model…'
                        : cfg.models.length === 0 ? 'All models (*) — select to restrict' : 'Add model…'
                    }
                    variant="outlined" size="small"
                  />
                )}
              />
              {models.length === 0 && (
                <Typography sx={{ fontSize: '0.68rem', color: textM, mt: 0.5 }}>
                  Use "Load Models" button above to populate model options from this app's API key.
                </Typography>
              )}
            </Box>

            {/* Per-model limits — only when onLimitChange is provided and there are named models */}
            {onLimitChange && namedModels.length > 0 && (
              <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: '0.65rem', color: textM, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.25 }}>
                  Per-model token limits
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: textM, mb: 0.5 }}>
                  Leave empty to use the app's daily limit. Set 0 to block this model.
                </Typography>
                {namedModels.map(model => {
                  const lim = cfg.model_limits[model] ?? { input_tokens: null, output_tokens: null };
                  return (
                    <Box key={model} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      p: 1, borderRadius: '6px',
                      bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      border: `1px solid ${border}`,
                    }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: textS, minWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {model}
                      </Typography>
                      <TextField
                        label="Input limit"
                        type="number"
                        size="small"
                        value={lim.input_tokens ?? ''}
                        onChange={(e) => onLimitChange(key, model, 'input_tokens', e.target.value === '' ? null : Math.max(0, parseInt(e.target.value) || 0))}
                        sx={{ width: 140 }}
                        slotProps={{ htmlInput: { min: 0 }, inputLabel: { shrink: true } }}
                      />
                      <TextField
                        label="Output limit"
                        type="number"
                        size="small"
                        value={lim.output_tokens ?? ''}
                        onChange={(e) => onLimitChange(key, model, 'output_tokens', e.target.value === '' ? null : Math.max(0, parseInt(e.target.value) || 0))}
                        sx={{ width: 140 }}
                        slotProps={{ htmlInput: { min: 0 }, inputLabel: { shrink: true } }}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // ── Usage bar helper ──────────────────────────────────────────────────────
  const UsageBar: React.FC<{ used: number; limit: number; label: string; color: string }> = ({ used, limit, label, color }) => {
    const rawPct = limit > 0 ? (used / limit) * 100 : 0;
    const pct = Math.min(rawPct, 100);
    const overLimit = limit > 0 && used > limit;
    const fmt = (n: number) => n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M' : n >= 1_000 ? (n / 1_000).toFixed(0) + 'K' : n.toString();
    const warn = pct > 85;
    const barColor = overLimit ? '#EF4444' : warn ? '#F59E0B' : color;
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.72rem', color: textM, fontWeight: 500 }}>{label}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: overLimit ? '#EF4444' : warn ? '#F59E0B' : textS, fontWeight: 600 }}>
            {fmt(used)}{limit > 0 ? ` / ${fmt(limit)}` : ''}
            {limit > 0 && (
              <Box component="span" sx={{ color: textM, ml: 0.5 }}>
                ({pct.toFixed(0)}%{overLimit ? ' — over limit' : ''})
              </Box>
            )}
          </Typography>
        </Box>
        <Box sx={{ height: 5, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: barColor, borderRadius: '3px', transition: 'width 0.3s ease' }} />
        </Box>
      </Box>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: textP, letterSpacing: '-0.02em' }}>
            Applications
          </Typography>
          <Typography sx={{ color: textS, fontSize: '0.875rem', mt: 0.25 }}>
            {apps.length} registered application{apps.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch size="small" checked={productionOnly} onChange={(e) => setProductionOnly(e.target.checked)}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#F59E0B' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#F59E0B' } }} />
            }
            label={<Typography sx={{ fontSize: '0.8rem', color: textS }}>Production only</Typography>}
          />
          {user?.role === 'super_admin' && apps.filter(a => a.openai_credits_exhausted).length > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={retryingAllCreditChecks ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 14 }} />}
              onClick={async () => {
                setRetryingAllCreditChecks(true);
                try {
                  const result = await apiService.retryCreditChecks();
                  if (result.message) {
                    showSnack(result.message, 'info');
                  } else {
                    const stillCount = result.still_exhausted_count ?? 0;
                    showSnack(
                      stillCount > 0
                        ? `Rechecked ${result.probed} app(s) — ${stillCount} still exhausted`
                        : `Rechecked ${result.probed} app(s) — all credits restored`,
                      stillCount > 0 ? 'error' : 'success',
                    );
                  }
                  loadApps();
                } catch (e: any) {
                  showSnack(e.response?.data?.detail || 'Failed to retry credit checks', 'error');
                } finally {
                  setRetryingAllCreditChecks(false);
                }
              }}
              disabled={retryingAllCreditChecks}
              sx={{ fontSize: '0.75rem', textTransform: 'none', borderColor: '#EF4444', color: '#EF4444' }}
            >
              Retry All ({apps.filter(a => a.openai_credits_exhausted).length} exhausted)
            </Button>
          )}
          {user?.permissions?.can_create_apps && (
            <Button data-tutorial="apps-register-btn" variant="contained" startIcon={<AddOutlined />} onClick={() => setOpenCreate(true)}>
              Register App
            </Button>
          )}
        </Box>
      </Box>

      {/* Table */}
      <Paper data-tutorial="apps-table" sx={{ border: `1px solid ${border}`, boxShadow: 'none', borderRadius: '8px', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {['App Name', 'Auth Key', 'Status', 'Input Limit', 'Output Limit', 'Today\'s Usage', 'Endpoints', 'Created', ''].map(h => (
                  <TableCell key={h} align={h === '' ? 'right' : 'left'}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: textM, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      {h}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 8 ? 56 : '80%'} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (productionOnly ? apps.filter(a => a.is_production) : apps).map((app) => {
                // Uses the app's own token_limit_input/output straight from the
                // database — the same values check_token_limits() actually
                // enforces. A per-model cap is a separate, independent check on
                // the backend (never summed against the app-level cap), so
                // deriving a smaller "effective" limit from it here doesn't
                // match real enforcement and only produces a different number
                // than the Input/Output Limit columns right next to it.
                const usageIn  = app.usage?.input_tokens  ?? 0;
                const usageOut = app.usage?.output_tokens ?? 0;
                const usagePct = (app.token_limit_input + app.token_limit_output) > 0
                  ? Math.round(((usageIn + usageOut) / (app.token_limit_input + app.token_limit_output)) * 100)
                  : 0;
                return (
                  <TableRow key={app.app_id} sx={{
                    opacity: app.pending_action ? 0.55 : 1,
                    transition: 'opacity 0.2s',
                    borderLeft: app.pending_action === 'delete'
                      ? '3px solid #D97706'
                      : app.pending_action === 'rename'
                        ? '3px solid #3B82F6'
                        : '3px solid transparent',
                    '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)' },
                  }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', color: textP }}>{app.app_name}</Typography>
                        {app.api_key_invalid && (
                          <Chip label="Invalid Key" size="small" sx={{ bgcolor: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2', color: '#EF4444', fontWeight: 600, fontSize: '0.65rem', height: 18, border: 'none' }} />
                        )}
                        {app.openai_credits_exhausted && (
                          <Tooltip title={app.openai_credits_exhausted_at ? `OpenAI returned 429 insufficient_quota at ${new Date(app.openai_credits_exhausted_at).toLocaleString()}. Recharge the OpenAI account or raise its hard limit.` : 'OpenAI account is out of credit.'}>
                            <Chip label="Credits Exhausted" size="small" sx={{ bgcolor: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2', color: '#EF4444', fontWeight: 600, fontSize: '0.65rem', height: 18, border: 'none' }} />
                          </Tooltip>
                        )}
                        {app.pending_action === 'delete' && (
                          <Chip label="Pending Deletion" size="small" sx={{ bgcolor: isDark ? 'rgba(245,158,11,0.15)' : '#fef3c7', color: '#D97706', fontWeight: 600, fontSize: '0.65rem', height: 18, border: 'none' }} />
                        )}
                        {app.pending_action === 'rename' && (
                          <Chip label="Pending Rename" size="small" sx={{ bgcolor: isDark ? 'rgba(59,130,246,0.15)' : '#dbeafe', color: '#3B82F6', fontWeight: 600, fontSize: '0.65rem', height: 18, border: 'none' }} />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        {(app.department || app.project_head) && (
                          <Typography sx={{ fontSize: '0.75rem', color: textM }}>
                            {[app.department, app.project_head].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, ml: (app.department || app.project_head) ? 0.5 : 0 }}>
                          {app.masked_openai_key && (
                            <Tooltip title="OpenAI configured" placement="top">
                              <Chip 
                                label="OpenAI" 
                                size="small" 
                                sx={{ 
                                  bgcolor: isDark ? 'rgba(16,185,129,0.12)' : '#d1fae5', 
                                  color: '#10B981', 
                                  fontWeight: 600, 
                                  fontSize: '0.6rem', 
                                  height: 16, 
                                  border: 'none',
                                  '& .MuiChip-label': { px: 0.75 }
                                }} 
                              />
                            </Tooltip>
                          )}
                          {app.masked_anthropic_key && (
                            <Tooltip title="Anthropic configured" placement="top">
                              <Chip 
                                label="Anthropic" 
                                size="small" 
                                sx={{ 
                                  bgcolor: isDark ? 'rgba(168,85,247,0.12)' : '#f3e8ff', 
                                  color: '#A855F7', 
                                  fontWeight: 600, 
                                  fontSize: '0.6rem', 
                                  height: 16, 
                                  border: 'none',
                                  '& .MuiChip-label': { px: 0.75 }
                                }} 
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {app.masked_auth_key ? (
                        <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.75rem', color: textS, letterSpacing: '0.04em' }}>
                          {app.masked_auth_key}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: textM }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={app.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            bgcolor: app.is_active ? (isDark ? 'rgba(34,197,94,0.12)' : '#dcfce7') : (isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'),
                            color: app.is_active ? '#22C55E' : textS,
                            fontWeight: 500, fontSize: '0.7rem', height: 22, border: 'none',
                          }}
                        />
                        {app.is_production && (
                          <Chip
                            label="Production"
                            size="small"
                            sx={{
                              bgcolor: isDark ? 'rgba(245,158,11,0.12)' : '#fef3c7',
                              color: '#F59E0B',
                              fontWeight: 500, fontSize: '0.7rem', height: 22, border: 'none',
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: textS, fontSize: '0.875rem' }}>{app.token_limit_input.toLocaleString()}</TableCell>
                    <TableCell sx={{ color: textS, fontSize: '0.875rem' }}>{app.token_limit_output.toLocaleString()}</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {/* Always render the bar, even at 0% - matches the
                          app-detail modal's UsageBar, which never special-
                          cases zero either. Previously this cell showed "—"
                          for no usage while the modal showed "0%" for the
                          exact same app/day, two different renderings of
                          the same state. */}
                      <Box>
                        <Typography sx={{ fontSize: '0.72rem', color: usagePct > 100 ? '#EF4444' : textS, fontWeight: 500, mb: 0.25 }}>
                          {Math.min(usagePct, 100)}% used{usagePct > 100 ? ' (over limit)' : ''}
                        </Typography>
                        <Box sx={{ height: 4, bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                          <Box sx={{ width: `${Math.min(usagePct, 100)}%`, height: '100%', bgcolor: usagePct > 100 ? '#EF4444' : usagePct > 85 ? '#F59E0B' : '#22C55E', borderRadius: '2px' }} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {app.permissions && Object.keys(app.permissions).length > 0 ? (
                        <Typography sx={{ fontSize: '0.8rem', color: textS }}>
                          {Object.keys(app.permissions).length} endpoint{Object.keys(app.permissions).length !== 1 ? 's' : ''}
                        </Typography>
                      ) : (
                        <Chip label="v1" size="small" sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6', color: textM, fontSize: '0.7rem', height: 20, border: 'none' }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: textS, fontSize: '0.8rem' }}>
                      {new Date(app.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => openDetailsDialog(app)} size="small" sx={{ color: textM, '&:hover': { color: textP } }}>
                        <Visibility fontSize="small" />
                      </IconButton>
                      {user?.permissions?.can_create_apps && (
                        <IconButton onClick={() => openEditDialog(app)} size="small" sx={{ color: textM, '&:hover': { color: '#3B82F6' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {user?.role === 'super_admin' && (
                        app.pending_action === 'delete' ? (
                          <Tooltip title="Cancel pending deletion">
                            <IconButton onClick={() => handleCancelDeletion(app)} size="small" sx={{ color: '#D97706', '&:hover': { color: '#B45309' } }}>
                              <CancelOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : app.pending_action === 'rename' ? (
                          <Tooltip title="Cancel pending rename">
                            <IconButton onClick={() => handleCancelRename(app)} size="small" sx={{ color: '#3B82F6', '&:hover': { color: '#2563EB' } }}>
                              <CancelOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Initiate deletion (requires 2 super admin approvals)">
                            <IconButton onClick={() => handleDelete(app)} size="small" sx={{ color: textM, '&:hover': { color: '#EF4444' } }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!tableLoading && apps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6, color: textM }}>
                    No applications registered yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ═══════════════════════════════════════════════════════════════════════
          Register App Dialog
      ════════════════════════════════════════════════════════════════════════ */}
      <Dialog open={openCreate} onClose={() => { setOpenCreate(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${border}`, pb: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: textP }}>Register New Application</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: textM, fontWeight: 400, mt: 0.25 }}>Configure app details and endpoint permissions</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ ...sectionLabel, mb: 1 }}>App Info</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="App Name" fullWidth margin="dense" value={formData.app_name}
              onChange={(e) => setFormData({ ...formData, app_name: e.target.value })} />
            <TextField label="Department" fullWidth margin="dense" value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })} />
            <Autocomplete<{ name: string; email: string }> fullWidth options={dirOptions} loading={dirLoading}
              getOptionLabel={(o) => o.name} isOptionEqualToValue={(a, b) => a.email === b.email}
              onInputChange={(_, v) => searchDir(v)}
              onChange={(_, v) => setFormData({ ...formData, project_head: v?.name ?? '' })}
              renderInput={(p) => (
                <TextField {...p} label="Project Head" margin="dense"
                  helperText="Defaults to you if left blank"
                  slotProps={{ ...p.slotProps, input: { ...p.slotProps.input, endAdornment: <>{dirLoading && <CircularProgress size={14} />}{p.slotProps.input.endAdornment}</> } }} />
              )}
            />
          </Box>

          <Typography sx={{ ...sectionLabel, mt: 2, mb: 0.5 }}>Provider API Keys</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: textS, mb: 1 }}>At least one provider key is required. Add both to enable all models.</Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip 
              label="OpenAI" 
              size="small" 
              sx={{ 
                bgcolor: formData.openai_api_key ? (isDark ? 'rgba(16,185,129,0.12)' : '#d1fae5') : (isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'),
                color: formData.openai_api_key ? '#10B981' : textM,
                fontWeight: 600, 
                fontSize: '0.65rem', 
                height: 18,
                border: 'none',
                '& .MuiChip-label': { px: 1 }
              }} 
            />
          </Box>
          <TextField label="OpenAI API Key" type="password" fullWidth margin="dense"
            value={formData.openai_api_key} placeholder="sk-..."
            onChange={(e) => { setFormData({ ...formData, openai_api_key: e.target.value }); setModelsFetched(false); setAvailableModels([]); }}
            helperText="For GPT, o1, DALL-E, Whisper, embeddings" />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, mt: 1.5 }}>
            <Chip 
              label="Anthropic" 
              size="small" 
              sx={{ 
                bgcolor: formData.anthropic_api_key ? (isDark ? 'rgba(168,85,247,0.12)' : '#f3e8ff') : (isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'),
                color: formData.anthropic_api_key ? '#A855F7' : textM,
                fontWeight: 600, 
                fontSize: '0.65rem', 
                height: 18,
                border: 'none',
                '& .MuiChip-label': { px: 1 }
              }} 
            />
          </Box>
          <TextField label="Anthropic API Key" type="password" fullWidth margin="dense"
            value={formData.anthropic_api_key} placeholder="sk-ant-..."
            onChange={(e) => { setFormData({ ...formData, anthropic_api_key: e.target.value }); setModelsFetched(false); setAvailableModels([]); }}
            helperText="For Claude models" />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
            <Button variant="outlined" size="small" onClick={handleFetchModelsClick}
              disabled={(!formData.openai_api_key && !formData.anthropic_api_key) || fetchingModels}
              startIcon={fetchingModels ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 16 }} />}
              sx={{ whiteSpace: 'nowrap', borderColor: modelsFetched ? '#22C55E' : undefined, color: modelsFetched ? '#22C55E' : undefined }}>
              {fetchingModels ? 'Fetching…' : modelsFetched ? `${availableModels.length} models` : 'Fetch Models'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField label="Daily Input Token Limit" type="number" fullWidth margin="dense"
              value={formData.token_limit_input}
              onChange={(e) => setFormData({ ...formData, token_limit_input: parseInt(e.target.value) })} />
            <TextField label="Daily Output Token Limit" type="number" fullWidth margin="dense"
              value={formData.token_limit_output}
              onChange={(e) => setFormData({ ...formData, token_limit_output: parseInt(e.target.value) })} />
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: textM, mt: 1.5, mb: 0.5, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
            Optional weekly cap
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Weekly Input Cap" type="number" fullWidth margin="dense" size="small"
              placeholder="No cap"
              value={formData.weekly_token_limit_input}
              onChange={(e) => setFormData({ ...formData, weekly_token_limit_input: e.target.value })} />
            <TextField label="Weekly Output Cap" type="number" fullWidth margin="dense" size="small"
              placeholder="No cap"
              value={formData.weekly_token_limit_output}
              onChange={(e) => setFormData({ ...formData, weekly_token_limit_output: e.target.value })} />
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: textM, mt: 1.5, mb: 0.5, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
            Optional monthly cap
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Monthly Input Cap" type="number" fullWidth margin="dense" size="small"
              placeholder="No cap"
              value={formData.monthly_token_limit_input}
              onChange={(e) => setFormData({ ...formData, monthly_token_limit_input: e.target.value })} />
            <TextField label="Monthly Output Cap" type="number" fullWidth margin="dense" size="small"
              placeholder="No cap"
              value={formData.monthly_token_limit_output}
              onChange={(e) => setFormData({ ...formData, monthly_token_limit_output: e.target.value })} />
          </Box>
          <Box sx={{ mt: 1.5, mb: 0.5 }}>
            <FormControlLabel
              control={
                <Switch checked={formData.is_production} onChange={(e) => setFormData({ ...formData, is_production: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#F59E0B' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#F59E0B' } }} />
              }
              label={<Typography sx={{ fontSize: '0.875rem', color: textS }}>Production app — ops gets ProxyWrapper limit alerts for this app</Typography>}
            />
          </Box>

          <Divider sx={{ my: 2.5 }} />
          <Typography sx={{ ...sectionLabel, mb: 0.5 }}>
            App Viewers <Box component="span" sx={{ fontSize: '0.7rem', color: textM, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(required — max 5)</Box>
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: textS, mb: 1.5 }}>Org users who can view token usage for this app. Defaults to you if left blank.</Typography>
          {registerViewers.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
              {registerViewers.map((email) => (
                <Chip key={email} label={email} size="small"
                  onDelete={() => setRegisterViewers(prev => prev.filter(e => e !== email))}
                  avatar={<Avatar sx={{ bgcolor: '#3B82F6', fontSize: '0.6rem' }}>{email[0].toUpperCase()}</Avatar>}
                  sx={{ bgcolor: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff', color: isDark ? '#93C5FD' : '#1d4ed8', fontSize: '0.75rem', border: isDark ? '1px solid rgba(59,130,246,0.25)' : '1px solid #bfdbfe' }} />
              ))}
            </Box>
          )}
          {registerViewers.length < 5 && (
            <Autocomplete<{ name: string; email: string; photo?: string | null }> fullWidth size="small"
              options={regViewerDirOptions.filter(o => !registerViewers.includes(o.email.toLowerCase()))}
              loading={regViewerDirLoading} value={regViewerDirValue}
              getOptionLabel={(o) => `${o.name} (${o.email})`} isOptionEqualToValue={(a, b) => a.email === b.email}
              onInputChange={(_, v) => searchRegViewerDir(v)}
              onChange={(_, v) => { if (v) { setRegisterViewers(p => [...p, v.email.toLowerCase()]); setRegViewerDirValue(null); setRegViewerDirOptions([]); } }}
              renderInput={(p) => (
                <TextField {...p} placeholder="Search org directory to add a viewer…" size="small"
                  slotProps={{ ...p.slotProps, input: { ...p.slotProps.input, endAdornment: <>{regViewerDirLoading && <CircularProgress size={14} />}{p.slotProps.input.endAdornment}</> } }} />
              )}
            />
          )}

          <Divider sx={{ my: 2.5 }} />
          <Typography sx={{ ...sectionLabel, mb: 0.5 }}>Endpoint Permissions</Typography>
          {!modelsFetched && <Typography sx={{ fontSize: '0.78rem', color: '#F59E0B', mb: 1.5 }}>Fetch models first to populate model options per endpoint.</Typography>}
          {modelsFetched && <Typography sx={{ fontSize: '0.78rem', color: textS, mb: 1.5 }}>Enable endpoints, select models, and optionally set per-model token limits.</Typography>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filterEndpointsByProviders(
              endpointCatalog,
              classifiedModels,
              !!formData.openai_api_key,
              !!formData.anthropic_api_key
            ).map((entry) => {
              const cfg = endpointPerms[entry.key] || { enabled: false, models: [], model_limits: {} };
              const models = classifiedModels[entry.key] ?? availableModels;
              return renderEndpointRow(
                entry, 
                cfg, 
                models, 
                toggleEndpoint, 
                setEndpointModels, 
                setEndpointModelLimit,
                !!formData.openai_api_key,
                !!formData.anthropic_api_key
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setOpenCreate(false); resetForm(); }} disabled={registering} sx={{ color: textS }}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained"
            disabled={!formData.app_name || (!formData.openai_api_key && !formData.anthropic_api_key) || !anyEndpointEnabled || registering}
            startIcon={registering ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}>
            {registering ? 'Registering…' : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════════
          Edit App Dialog
      ════════════════════════════════════════════════════════════════════════ */}
      <Dialog open={openEdit} onClose={closeEditDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${border}`, pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: textP }}>Edit Application</Typography>
            {selectedApp && user?.role === 'super_admin' && !selectedApp.pending_action && (
              <Tooltip title="Rename application (requires 2 super admin approvals)">
                <IconButton
                  size="small"
                  onClick={() => openRenameDialog(selectedApp)}
                  sx={{ p: 0.5, color: textM, '&:hover': { color: '#3B82F6', bgcolor: 'rgba(59,130,246,0.08)' } }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography sx={{ fontSize: '0.8rem', color: textM, fontWeight: 400, mt: 0.25 }}>Update app details, endpoint permissions, and per-model token limits</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ ...sectionLabel, mb: 1 }}>App Info</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="App Name" fullWidth margin="dense" value={selectedApp?.app_name ?? ''} disabled
              sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: textS } }} />
            <TextField label="Department" fullWidth margin="dense" value={editForm.department}
              onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} />
            <Autocomplete<{ name: string; email: string }> fullWidth options={editDirOptions} loading={editDirLoading}
              getOptionLabel={(o) => o.name} isOptionEqualToValue={(a, b) => a.email === b.email}
              inputValue={editForm.project_head}
              onInputChange={(_, v, r) => { if (r === 'input') { setEditForm(p => ({ ...p, project_head: v })); searchEditDir(v); } }}
              onChange={(_, v) => setEditForm(p => ({ ...p, project_head: v?.name ?? '' }))}
              renderInput={(p) => (
                <TextField {...p} label="Project Head" margin="dense"
                  slotProps={{ ...p.slotProps, input: { ...p.slotProps.input, endAdornment: <>{editDirLoading && <CircularProgress size={14} />}{p.slotProps.input.endAdornment}</> } }} />
              )}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <TextField label="Daily Input Token Limit" type="number" fullWidth margin="dense"
              value={editForm.token_limit_input}
              onChange={(e) => setEditForm({ ...editForm, token_limit_input: parseInt(e.target.value) })} />
            <TextField label="Daily Output Token Limit" type="number" fullWidth margin="dense"
              value={editForm.token_limit_output}
              onChange={(e) => setEditForm({ ...editForm, token_limit_output: parseInt(e.target.value) })} />
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: textM, mt: 1.5, mb: 0.5, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
            Weekly cap (optional)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Weekly Input Cap" type="number" fullWidth margin="dense" size="small"
              placeholder="No cap"
              value={editForm.weekly_token_limit_input}
              onChange={(e) => setEditForm({ ...editForm, weekly_token_limit_input: e.target.value })} />
            <TextField label="Weekly Output Cap" type="number" fullWidth margin="dense" size="small"
              placeholder="No cap"
              value={editForm.weekly_token_limit_output}
              onChange={(e) => setEditForm({ ...editForm, weekly_token_limit_output: e.target.value })} />
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: textM, mt: 1.5, mb: 0.5, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>
            Monthly cap (optional)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Monthly Input Cap" type="number" fullWidth margin="dense" size="small"
              placeholder="No cap"
              value={editForm.monthly_token_limit_input}
              onChange={(e) => setEditForm({ ...editForm, monthly_token_limit_input: e.target.value })} />
            <TextField label="Monthly Output Cap" type="number" fullWidth margin="dense" size="small"
              placeholder="No cap"
              value={editForm.monthly_token_limit_output}
              onChange={(e) => setEditForm({ ...editForm, monthly_token_limit_output: e.target.value })} />
          </Box>
          <Box sx={{ mt: 1.5, mb: 0.5 }}>
            <FormControlLabel
              control={
                <Switch checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22C55E' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22C55E' } }} />
              }
              label={<Typography sx={{ fontSize: '0.875rem', color: textS }}>{editForm.is_active ? 'Active' : 'Inactive'}</Typography>}
            />
          </Box>
          <Box sx={{ mb: 0.5 }}>
            <FormControlLabel
              control={
                <Switch checked={editForm.is_production} onChange={(e) => setEditForm({ ...editForm, is_production: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#F59E0B' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#F59E0B' } }} />
              }
              label={<Typography sx={{ fontSize: '0.875rem', color: textS }}>Production app{editForm.is_production ? ' — ops gets ProxyWrapper limit alerts for this app' : ''}</Typography>}
            />
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* Configure Providers */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography sx={{ ...sectionLabel }}>Provider Configuration</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: textM, mt: 0.25 }}>
                  Add or update LLM provider API keys for this application
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowEditProviders(!showEditProviders)}
                startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: '0.75rem', textTransform: 'none', borderColor: border, color: textS }}
              >
                {showEditProviders ? 'Hide' : 'Configure'}
              </Button>
            </Box>

            {/* Current provider status */}
            <Box sx={{ display: 'flex', gap: 2, mb: showEditProviders ? 2 : 0 }}>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: subBg, border: `1px solid ${border}`, borderRadius: '6px', px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: textM, minWidth: 70 }}>OpenAI</Typography>
                {selectedApp?.masked_openai_key ? (
                  <>
                    <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.75rem', color: textP, flex: 1 }}>
                      {selectedApp.masked_openai_key}
                    </Typography>
                    <Chip label="Configured" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: isDark ? 'rgba(34,197,94,0.12)' : '#dcfce7', color: '#22C55E', border: 'none' }} />
                  </>
                ) : (
                  <Typography sx={{ fontSize: '0.75rem', color: textM, fontStyle: 'italic', flex: 1 }}>Not configured</Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: subBg, border: `1px solid ${border}`, borderRadius: '6px', px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: textM, minWidth: 70 }}>Anthropic</Typography>
                {selectedApp?.masked_anthropic_key ? (
                  <>
                    <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.75rem', color: textP, flex: 1 }}>
                      {selectedApp.masked_anthropic_key}
                    </Typography>
                    <Chip label="Configured" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: isDark ? 'rgba(34,197,94,0.12)' : '#dcfce7', color: '#22C55E', border: 'none' }} />
                  </>
                ) : (
                  <Typography sx={{ fontSize: '0.75rem', color: textM, fontStyle: 'italic', flex: 1 }}>Not configured</Typography>
                )}
              </Box>
            </Box>

            {/* Provider key inputs (shown when Configure is clicked) */}
            {showEditProviders && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc', borderRadius: '8px', border: `1px solid ${border}` }}>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: textP, mb: 0.5 }}>
                    OpenAI API Key {selectedApp?.masked_openai_key && <Typography component="span" sx={{ fontSize: '0.7rem', color: textM }}>(leave empty to keep current)</Typography>}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={selectedApp?.masked_openai_key ? 'Enter new key to replace existing' : 'sk-...'}
                    type="password"
                    value={editOpenAIKey}
                    onChange={e => setEditOpenAIKey(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem', fontFamily: '"JetBrains Mono","Courier New",monospace' } }}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: textP, flex: 1 }}>
                      Anthropic API Key {selectedApp?.masked_anthropic_key && <Typography component="span" sx={{ fontSize: '0.7rem', color: textM }}>(leave empty to keep current)</Typography>}
                    </Typography>
                    {selectedApp?.masked_anthropic_key && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => setRemoveAnthropicKey(!removeAnthropicKey)}
                        sx={{ fontSize: '0.7rem', textTransform: 'none', height: 24, flexShrink: 0 }}
                      >
                        {removeAnthropicKey ? 'Keep Key' : 'Remove'}
                      </Button>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={removeAnthropicKey}
                    placeholder={selectedApp?.masked_anthropic_key ? 'Enter new key to replace existing' : 'sk-ant-...'}
                    type="password"
                    value={editAnthropicKey}
                    onChange={e => setEditAnthropicKey(e.target.value)}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { fontSize: '0.85rem', fontFamily: '"JetBrains Mono","Courier New",monospace' },
                      ...(removeAnthropicKey && { opacity: 0.6 })
                    }}
                  />
                  {removeAnthropicKey && (
                    <Typography sx={{ fontSize: '0.7rem', color: '#ef4444', mt: 0.5, fontWeight: 500 }}>
                      ⚠️ Anthropic key will be removed
                    </Typography>
                  )}
                </Box>
                <Alert severity="info" sx={{ fontSize: '0.72rem', py: 0.5, '& .MuiAlert-message': { py: 0 } }}>
                  Provider keys will be updated when you save changes. Leave fields empty to keep existing keys.
                </Alert>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* Load models for edit */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box>
              <Typography sx={{ ...sectionLabel }}>Endpoint Permissions & Model Limits</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: textM, mt: 0.25 }}>
                {editModelsFetched
                  ? `${editAvailableModels.length} models loaded — select and set per-model limits below`
                  : 'Load models from the app\'s API key to add or change model assignments'}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={handleFetchEditModels}
              disabled={editFetchingModels}
              startIcon={editFetchingModels ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 15 }} />}
              sx={{
                whiteSpace: 'nowrap', flexShrink: 0,
                borderColor: editModelsFetched ? '#22C55E' : border,
                color: editModelsFetched ? '#22C55E' : textS,
                '&:hover': { borderColor: editModelsFetched ? '#22C55E' : textP, color: editModelsFetched ? '#22C55E' : textP },
              }}
            >
              {editFetchingModels ? 'Loading…' : editModelsFetched ? `${editAvailableModels.length} models` : 'Load Models'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filterEndpointsByProviders(
              endpointCatalog,
              editClassifiedModels,
              !!selectedApp?.masked_openai_key,
              !!selectedApp?.masked_anthropic_key
            ).map((entry) => {
              const cfg = editEndpointPerms[entry.key] || { enabled: false, models: [], model_limits: {} };
              const models = editClassifiedModels[entry.key] ?? editAvailableModels;
              return renderEndpointRow(
                entry, 
                cfg, 
                models, 
                toggleEditEndpoint, 
                setEditEndpointModels, 
                setEditEndpointModelLimit,
                !!selectedApp?.masked_openai_key,
                !!selectedApp?.masked_anthropic_key
              );
            })}
            {Object.keys(editEndpointPerms)
              .filter(k => !endpointCatalog.some(e => e.key === k))
              .map((key) => {
                const cfg = editEndpointPerms[key];
                return renderEndpointRow(
                  { key, description: '(custom)' }, 
                  cfg, 
                  editAvailableModels, 
                  toggleEditEndpoint, 
                  setEditEndpointModels, 
                  setEditEndpointModelLimit,
                  !!selectedApp?.masked_openai_key,
                  !!selectedApp?.masked_anthropic_key
                );
              })}
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {/* App Viewers */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ ...sectionLabel }}>App Viewers ({(selectedApp?.viewers ?? []).length}/5)</Typography>
            {(selectedApp?.viewers ?? []).length > 0 && (
              <LinearProgress variant="determinate" value={((selectedApp?.viewers ?? []).length / 5) * 100}
                sx={{ width: 64, borderRadius: 1, bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: (selectedApp?.viewers ?? []).length >= 5 ? '#EF4444' : '#3B82F6' } }} />
            )}
          </Box>
          <Typography sx={{ fontSize: '0.78rem', color: textS, mb: 1.5 }}>Org users who can view token usage for this app.</Typography>
          {(selectedApp?.viewers ?? []).length === 0 ? (
            <Typography sx={{ fontSize: '0.8rem', color: textM, mb: 1.5 }}>No viewers added yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
              {(selectedApp?.viewers ?? []).map((email) => (
                <Box key={email} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, bgcolor: subBg, borderRadius: '6px', border: `1px solid ${border}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: '#3B82F6' }}>{email[0].toUpperCase()}</Avatar>
                    <Typography sx={{ fontSize: '0.8rem', color: textP }}>{email}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveViewer(email)} disabled={removingViewer === email}
                    sx={{ color: textM, '&:hover': { color: '#EF4444' }, p: 0.5 }}>
                    {removingViewer === email ? <CircularProgress size={14} /> : <PersonRemoveOutlined sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          {(selectedApp?.viewers ?? []).length < 5 ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Autocomplete<{ name: string; email: string; photo?: string | null }> fullWidth size="small"
                options={viewerDirOptions} loading={viewerDirLoading} value={viewerDirValue}
                getOptionLabel={(o) => `${o.name} (${o.email})`} isOptionEqualToValue={(a, b) => a.email === b.email}
                onInputChange={(_, v) => searchViewerDir(v)}
                onChange={(_, v) => { setViewerDirValue(v); setViewerEmail(v?.email ?? ''); }}
                renderInput={(p) => (
                  <TextField {...p} placeholder="Search org directory to add viewer…" size="small"
                    slotProps={{ ...p.slotProps, input: { ...p.slotProps.input, endAdornment: <>{viewerDirLoading && <CircularProgress size={14} />}{p.slotProps.input.endAdornment}</> } }} />
                )}
              />
              <Button variant="contained" size="small" onClick={handleAddViewer}
                disabled={!viewerEmail || addingViewer}
                startIcon={addingViewer ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <PersonAdd sx={{ fontSize: 16 }} />}
                sx={{ whiteSpace: 'nowrap', bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, '&.Mui-disabled': { bgcolor: isDark ? 'rgba(59,130,246,0.2)' : '#bfdbfe', color: isDark ? '#93C5FD' : '#1d4ed8' } }}>
                {addingViewer ? 'Adding…' : 'Add Viewer'}
              </Button>
            </Box>
          ) : (
            <Alert severity="info" sx={{ fontSize: '0.8rem', py: 0.5 }}>Maximum 5 viewers reached.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeEditDialog} disabled={saving} sx={{ color: textS }}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════════
          App Details Dialog
      ════════════════════════════════════════════════════════════════════════ */}
      <Dialog open={openDetails} onClose={closeDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${border}`, pb: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: textP }}>{selectedApp?.app_name}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: textM, fontWeight: 400, mt: 0.25 }}>
            {[selectedApp?.department, selectedApp?.project_head].filter(Boolean).join(' · ')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedApp && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* Meta info */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {[
                  { label: 'Status', content: (
                    <Chip label={selectedApp.is_active ? 'Active' : 'Inactive'} size="small"
                      sx={{ bgcolor: selectedApp.is_active ? (isDark ? 'rgba(34,197,94,0.12)' : '#dcfce7') : (isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'), color: selectedApp.is_active ? '#22C55E' : textS, fontWeight: 500, fontSize: '0.7rem', height: 22, border: 'none' }} />
                  )},
                  ...(selectedApp.is_production ? [{ label: 'Environment', content: (
                    <Chip label="Production" size="small"
                      sx={{ bgcolor: isDark ? 'rgba(245,158,11,0.12)' : '#fef3c7', color: '#F59E0B', fontWeight: 500, fontSize: '0.7rem', height: 22, border: 'none' }} />
                  )}] : []),
                  { label: 'Schema', content: (
                    <Chip label={`v${selectedApp.schema_version || 1}`} size="small"
                      sx={{ bgcolor: selectedApp.schema_version === 2 ? (isDark ? 'rgba(59,130,246,0.15)' : '#eff6ff') : (isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'), color: selectedApp.schema_version === 2 ? '#3B82F6' : textS, fontWeight: 600, fontSize: '0.7rem', height: 22, border: 'none' }} />
                  )},
                  { label: 'Created', content: <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: textP }}>{new Date(selectedApp.created_at).toLocaleDateString()}</Typography> },
                ].map(({ label, content }) => (
                  <Box key={label}>
                    <Typography sx={{ ...sectionLabel, mb: 0.5 }}>{label}</Typography>
                    {content}
                  </Box>
                ))}
              </Box>

              {/* Invalid provider keys warnings */}
              {(selectedApp.api_key_invalid || selectedApp.anthropic_key_invalid) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {selectedApp.api_key_invalid && (
                    <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2', border: '1px solid', borderColor: isDark ? 'rgba(239,68,68,0.3)' : '#fecaca', borderRadius: '8px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip 
                          label="OpenAI" 
                          size="small" 
                          sx={{ 
                            bgcolor: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2',
                            color: '#EF4444',
                            fontWeight: 600, 
                            fontSize: '0.6rem', 
                            height: 16,
                            border: 'none',
                            '& .MuiChip-label': { px: 0.75 }
                          }} 
                        />
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#EF4444' }}>
                          Invalid API Key
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', color: isDark ? '#FCA5A5' : '#B91C1C', mb: 1.5 }}>
                        OpenAI requests are being blocked. Use "Update Keys" above to fix.
                      </Typography>
                    </Box>
                  )}
                  {selectedApp.anthropic_key_invalid && (
                    <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2', border: '1px solid', borderColor: isDark ? 'rgba(239,68,68,0.3)' : '#fecaca', borderRadius: '8px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip 
                          label="Anthropic" 
                          size="small" 
                          sx={{ 
                            bgcolor: isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2',
                            color: '#EF4444',
                            fontWeight: 600, 
                            fontSize: '0.6rem', 
                            height: 16,
                            border: 'none',
                            '& .MuiChip-label': { px: 0.75 }
                          }} 
                        />
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#EF4444' }}>
                          Invalid API Key
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', color: isDark ? '#FCA5A5' : '#B91C1C', mb: 1.5 }}>
                        Anthropic requests are being blocked. Use "Update Keys" above to fix.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* OpenAI credits exhausted warning */}
              {selectedApp.openai_credits_exhausted && (
                <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2', border: '1px solid', borderColor: isDark ? 'rgba(239,68,68,0.3)' : '#fecaca', borderRadius: '8px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#EF4444', mb: 0.5 }}>
                        OpenAI Credits Exhausted
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: isDark ? '#FCA5A5' : '#B91C1C' }}>
                        OpenAI rejected this app's last request{selectedApp.openai_credits_exhausted_at ? ` at ${new Date(selectedApp.openai_credits_exhausted_at).toLocaleString()}` : ''}
                        {selectedApp.openai_credits_exhausted_message ? <>: <code>{selectedApp.openai_credits_exhausted_message}</code></> : ''}.
                        {' '}Recharge the OpenAI account, raise its hard limit, or raise its spend limit (a separate setting from balance) — this app will be down for end users until that's resolved. The flag clears automatically on the next successful OpenAI response.
                      </Typography>
                    </Box>
                    {user?.role === 'super_admin' && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={retryingCreditChecks ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 14 }} />}
                        onClick={async () => {
                          setRetryingCreditChecks(true);
                          try {
                            const result = await apiService.retryCreditCheckOneApp(selectedApp.app_id);
                            setSelectedApp(prev => prev ? {
                              ...prev,
                              openai_credits_exhausted: result.openai_credits_exhausted,
                              openai_credits_exhausted_message: result.openai_credits_exhausted_message ?? undefined,
                            } : prev);
                            showSnack(
                              result.openai_credits_exhausted
                                ? `Still exhausted: ${result.openai_credits_exhausted_message || 'insufficient_quota'}`
                                : 'Credits restored — app is unblocked',
                              result.openai_credits_exhausted ? 'error' : 'success',
                            );
                            loadApps();
                          } catch (e: any) {
                            showSnack(e.response?.data?.detail || 'Failed to retry credit check', 'error');
                          } finally {
                            setRetryingCreditChecks(false);
                          }
                        }}
                        disabled={retryingCreditChecks}
                        sx={{ fontSize: '0.7rem', textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#EF4444', color: '#EF4444' }}
                      >
                        Retry Check
                      </Button>
                    )}
                  </Box>
                </Box>
              )}

              {/* Anthropic credits exhausted warning */}
              {selectedApp.anthropic_credits_exhausted && (
                <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2', border: '1px solid', borderColor: isDark ? 'rgba(239,68,68,0.3)' : '#fecaca', borderRadius: '8px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#EF4444', mb: 0.5 }}>
                        Anthropic Credits Exhausted
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: isDark ? '#FCA5A5' : '#B91C1C' }}>
                        Anthropic rejected this app's last request with 429 (rate limit/insufficient credits){selectedApp.anthropic_credits_exhausted_at ? ` at ${new Date(selectedApp.anthropic_credits_exhausted_at).toLocaleString()}` : ''}. Recharge the Anthropic account — this app will be down for Anthropic model requests until that's resolved.
                      </Typography>
                    </Box>
                    {(user?.role === 'super_admin' || user?.role === 'admin') && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={retryingCreditChecks ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 14 }} />}
                        onClick={async () => {
                          setRetryingCreditChecks(true);
                          try {
                            await apiService.retryModelCheck(selectedApp.app_id);
                            showSnack('Model checks retried successfully', 'success');
                            loadApps();
                          } catch (e: any) {
                            showSnack(e.response?.data?.detail || 'Failed to retry model checks', 'error');
                          } finally {
                            setRetryingCreditChecks(false);
                          }
                        }}
                        disabled={retryingCreditChecks}
                        sx={{ fontSize: '0.7rem', textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#EF4444', color: '#EF4444' }}
                      >
                        Retry Check
                      </Button>
                    )}
                  </Box>
                </Box>
              )}

              {/* Pending deletion warning */}
              {selectedApp.pending_action === 'delete' && (
                <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(245,158,11,0.1)' : '#fffbeb', border: '1px solid', borderColor: isDark ? 'rgba(245,158,11,0.35)' : '#fcd34d', borderRadius: '8px' }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#D97706', mb: 0.5 }}>
                    Pending Deletion — Awaiting Second Approval
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: isDark ? '#FCD34D' : '#92400E', mb: 1.5 }}>
                    A super admin has requested deletion of this app. <strong>API traffic to this app is frozen (423 Locked) until a second super admin approves or cancels the request via the Requests page.</strong>
                  </Typography>
                  {user?.role === 'super_admin' && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleCancelDeletion(selectedApp)}
                      sx={{ fontSize: '0.75rem', borderColor: '#D97706', color: '#D97706', '&:hover': { bgcolor: 'rgba(217,119,6,0.08)', borderColor: '#B45309' } }}
                    >
                      Cancel Deletion
                    </Button>
                  )}
                </Box>
              )}

              {/* Pending rename warning */}
              {selectedApp.pending_action === 'rename' && (
                <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(59,130,246,0.1)' : '#eff6ff', border: '1px solid', borderColor: isDark ? 'rgba(59,130,246,0.35)' : '#93c5fd', borderRadius: '8px' }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#3B82F6', mb: 0.5 }}>
                    Pending Rename — Awaiting Second Approval
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: isDark ? '#93C5FD' : '#1E40AF', mb: 1.5 }}>
                    A super admin has requested to rename this app{selectedApp.pending_rename_new_name ? <> to <strong>{selectedApp.pending_rename_new_name}</strong></> : null}. <strong>API traffic to this app is frozen (423 Locked) until a second super admin approves or cancels the request via the Requests page.</strong>
                  </Typography>
                  {user?.role === 'super_admin' && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleCancelRename(selectedApp)}
                      sx={{ fontSize: '0.75rem', borderColor: '#3B82F6', color: '#3B82F6', '&:hover': { bgcolor: 'rgba(59,130,246,0.08)', borderColor: '#2563EB' } }}
                    >
                      Cancel Rename
                    </Button>
                  )}
                </Box>
              )}

              {/* Provider Keys Section */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ ...sectionLabel }}>Provider Keys</Typography>
                  {user?.role === 'super_admin' && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setShowUpdateKeys(true)}
                      sx={{ fontSize: '0.7rem', textTransform: 'none', borderColor: border, color: textS }}
                    >
                      Update Keys
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: subBg, border: `1px solid ${border}`, borderRadius: '6px', px: 1.5, py: 1 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: textM, minWidth: 80 }}>OpenAI</Typography>
                    <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.78rem', color: textP, flex: 1 }}>
                      {selectedApp.masked_openai_key || 'Not configured'}
                    </Typography>
                    {selectedApp.masked_openai_key && (
                      <>
                        <Chip label="Configured" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: isDark ? 'rgba(34,197,94,0.12)' : '#dcfce7', color: '#22C55E', border: 'none' }} />
                        {user?.role === 'super_admin' && (
                          <IconButton
                            size="small"
                            onClick={() => { setRemovalTarget('openai'); setConfirmRemoveKey(true); }}
                            sx={{ p: 0.5, color: '#EF4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: subBg, border: `1px solid ${border}`, borderRadius: '6px', px: 1.5, py: 1 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: textM, minWidth: 80 }}>Anthropic</Typography>
                    <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.78rem', color: textP, flex: 1 }}>
                      {selectedApp.masked_anthropic_key || 'Not configured'}
                    </Typography>
                    {selectedApp.masked_anthropic_key && (
                      <>
                        <Chip label="Configured" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: isDark ? 'rgba(34,197,94,0.12)' : '#dcfce7', color: '#22C55E', border: 'none' }} />
                        {user?.role === 'super_admin' && (
                          <IconButton
                            size="small"
                            onClick={() => { setRemovalTarget('anthropic'); setConfirmRemoveKey(true); }}
                            sx={{ p: 0.5, color: '#EF4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </>
                    )}
                  </Box>
                </Box>

                {/* Update Keys Dialog */}
                {showUpdateKeys && (
                  <Dialog open={showUpdateKeys} onClose={() => { setShowUpdateKeys(false); setNewOpenAIKey(''); setNewAnthropicKey(''); }} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ borderBottom: `1px solid ${border}` }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: textP }}>Update Provider Keys</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: textM, mt: 0.5 }}>Update OpenAI and/or Anthropic API keys for {selectedApp?.app_name}</Typography>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2.5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: textP, mb: 0.5 }}>OpenAI API Key</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="sk-... (leave empty to keep current)"
                            type="password"
                            value={newOpenAIKey}
                            onChange={e => setNewOpenAIKey(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem', fontFamily: '"JetBrains Mono","Courier New",monospace' } }}
                          />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: textP, mb: 0.5 }}>Anthropic API Key</Typography>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="sk-ant-... (leave empty to keep current)"
                            type="password"
                            value={newAnthropicKey}
                            onChange={e => setNewAnthropicKey(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem', fontFamily: '"JetBrains Mono","Courier New",monospace' } }}
                          />
                        </Box>
                        <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                          Leave a field empty to keep the existing key for that provider.
                        </Alert>
                      </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                      <Button onClick={() => { setShowUpdateKeys(false); setNewOpenAIKey(''); setNewAnthropicKey(''); }} disabled={updatingKeys} sx={{ color: textS }}>Cancel</Button>
                      <Button
                        onClick={async () => {
                          if (!selectedApp || (!newOpenAIKey.trim() && !newAnthropicKey.trim())) {
                            showSnack('Please provide at least one API key to update', 'error');
                            return;
                          }
                          setUpdatingKeys(true);
                          try {
                            const keys: { openai_api_key?: string; anthropic_api_key?: string } = {};
                            if (newOpenAIKey.trim()) keys.openai_api_key = newOpenAIKey.trim();
                            if (newAnthropicKey.trim()) keys.anthropic_api_key = newAnthropicKey.trim();
                            const updated = await apiService.updateAppKeys(selectedApp.app_id, keys);
                            setApps(prev => prev.map(a => a.app_id === selectedApp.app_id ? { ...a, ...updated } : a));
                            setSelectedApp({ ...selectedApp, ...updated });
                            setNewOpenAIKey('');
                            setNewAnthropicKey('');
                            setShowUpdateKeys(false);
                            showSnack('Provider keys updated successfully', 'success');
                          } catch (e: any) {
                            showSnack(e.response?.data?.detail || 'Failed to update keys', 'error');
                          } finally { setUpdatingKeys(false); }
                        }}
                        variant="contained"
                        disabled={updatingKeys || (!newOpenAIKey.trim() && !newAnthropicKey.trim())}
                        startIcon={updatingKeys ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                      >
                        {updatingKeys ? 'Updating…' : 'Update Keys'}
                      </Button>
                    </DialogActions>
                  </Dialog>
                )}
              </Box>

                              {/* Remove API Key Confirmation Dialog */}
                {confirmRemoveKey && (
                  <Dialog open={confirmRemoveKey} onClose={() => { setConfirmRemoveKey(false); setRemovalTarget(null); }} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ borderBottom: `1px solid ${border}` }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: textP }}>Remove {removalTarget === 'openai' ? 'OpenAI' : 'Anthropic'} API Key</Typography>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2.5 }}>
                      <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
                        This will remove the {removalTarget === 'openai' ? 'OpenAI' : 'Anthropic'} API key for this app. The app will no longer be able to access {removalTarget === 'openai' ? 'OpenAI' : 'Anthropic'} models until a new key is configured.
                      </Alert>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                      <Button onClick={() => { setConfirmRemoveKey(false); setRemovalTarget(null); }} disabled={updatingKeys} sx={{ color: textS }}>Cancel</Button>
                      <Button
                        onClick={async () => {
                          if (!selectedApp || !removalTarget) return;
                          setUpdatingKeys(true);
                          try {
                            const keys: any = {};
                            if (removalTarget === 'openai') {
                              keys.remove_openai_key = true;
                            } else {
                              keys.remove_anthropic_key = true;
                            }
                            const updated = await apiService.updateAppKeys(selectedApp.app_id, keys);
                            setApps(prev => prev.map(a => a.app_id === selectedApp.app_id ? { ...a, ...updated } : a));
                            setSelectedApp({ ...selectedApp, ...updated });
                            setConfirmRemoveKey(false);
                            setRemovalTarget(null);
                            showSnack(`${removalTarget === 'openai' ? 'OpenAI' : 'Anthropic'} key removed successfully`, 'success');
                          } catch (e: any) {
                            showSnack(e.response?.data?.detail || 'Failed to remove key', 'error');
                          } finally { setUpdatingKeys(false); }
                        }}
                        variant="contained"
                        color="error"
                        disabled={updatingKeys}
                        startIcon={updatingKeys ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
                      >
                        {updatingKeys ? 'Removing…' : 'Remove Key'}
                      </Button>
                    </DialogActions>
                  </Dialog>
                )}

              {/* Auth Key — super_admin only */}
              {user?.role === 'super_admin' && (
                <Box>
                  <Typography sx={{ ...sectionLabel, mb: 1 }}>Auth Key</Typography>
                  {viewedAuthKey ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC', border: `1px solid ${border}`, borderRadius: '6px', px: 1.5, py: 1 }}>
                      <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.78rem', color: textP, flex: 1, wordBreak: 'break-all', letterSpacing: showViewedAuthKey ? '0.02em' : '0.2em' }}>
                        {showViewedAuthKey ? viewedAuthKey : '•'.repeat(Math.min(viewedAuthKey.length, 48))}
                      </Typography>
                      <IconButton size="small" onClick={() => setShowViewedAuthKey(v => !v)} sx={{ color: textS, '&:hover': { color: textP } }}>
                        {showViewedAuthKey ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                      </IconButton>
                      <IconButton size="small" onClick={() => copyToClipboard(viewedAuthKey, 'Auth Key')} sx={{ color: textS, '&:hover': { color: textP } }}>
                        <ContentCopy sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={fetchingAuthKey}
                      onClick={async () => {
                        if (!selectedApp) return;
                        setFetchingAuthKey(true);
                        try {
                          const res = await apiService.getAppKey(selectedApp.app_id);
                          setViewedAuthKey(res.auth_key);
                        } catch {
                          showSnack('Failed to fetch auth key', 'error');
                        } finally { setFetchingAuthKey(false); }
                      }}
                      sx={{ borderColor: border, color: textS, fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      {fetchingAuthKey ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                      Reveal Auth Key
                    </Button>
                  )}
                </Box>
              )}

              {/* Today's usage */}
              {(selectedApp.usage?.input_tokens !== undefined || selectedApp.usage?.output_tokens !== undefined) && (
                <Box>
                  <Typography sx={{ ...sectionLabel, mb: 1.5 }}>Today's Usage</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* token_limit_input/output straight from the database -
                        the same values check_token_limits() enforces. A
                        per-model cap is a separate, independent backend check
                        (never summed against the app-level cap), so this used
                        to compute a smaller "effective" limit that didn't
                        match real enforcement or the Input/Output Limit
                        columns shown on the Apps table for this same app. */}
                    <UsageBar label="Input tokens"  used={selectedApp.usage?.input_tokens  ?? 0} limit={selectedApp.token_limit_input}  color="#22D3EE" />
                    <UsageBar label="Output tokens" used={selectedApp.usage?.output_tokens ?? 0} limit={selectedApp.token_limit_output} color="#A855F7" />
                  </Box>

                  {/* Per-model breakdown */}
                  {selectedApp.usage?.model_usage && Object.keys(selectedApp.usage.model_usage).length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ ...sectionLabel, mb: 1 }}>Per-model breakdown</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {Object.entries(selectedApp.usage.model_usage as Record<string, {
                          input_tokens: number; output_tokens: number; cached_tokens?: number; cache_write_tokens?: number;
                          cost?: {
                            total_cost: number; cache_savings: number; cache_write_cost: number;
                            short_context_cost: number; long_context_cost: number;
                          };
                        }>)
                          .sort(([, a], [, b]) => (b.input_tokens + b.output_tokens) - (a.input_tokens + a.output_tokens))
                          .map(([model, mu]) => {
                            const totalUsed = (mu.input_tokens ?? 0) + (mu.output_tokens ?? 0);
                            // Find model limit from endpoint permissions
                            let modelLimit: ModelLimit | null = null;
                            Object.values(selectedApp.permissions ?? {}).forEach(ep => {
                              const ml = ep.model_limits as Record<string, ModelLimit> | undefined;
                              if (ml?.[model]) modelLimit = ml[model];
                            });
                            const fmt = (n: number) => n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M' : n >= 1_000 ? (n / 1_000).toFixed(0) + 'K' : n.toString();
                            const cached = mu.cached_tokens ?? 0;
                            const cacheWrite = mu.cache_write_tokens ?? 0;
                            const nonCached = (mu.input_tokens ?? 0) - cached - cacheWrite;
                            const cost = mu.cost;
                            return (
                              <Box key={model} sx={{ p: 1.5, bgcolor: subBg, borderRadius: '6px', border: `1px solid ${border}` }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: textP }}>{model}</Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: textS }}>{fmt(totalUsed)} total</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                  <Box>
                                    <Typography sx={{ fontSize: '0.65rem', color: textM, mb: 0.25 }}>INPUT</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#22D3EE' }}>
                                      {fmt(mu.input_tokens ?? 0)}
                                      {modelLimit && (modelLimit as ModelLimit).input_tokens != null && <Box component="span" sx={{ color: textM, fontWeight: 400, ml: 0.5 }}>/ {fmt((modelLimit as ModelLimit).input_tokens as number)}</Box>}
                                    </Typography>
                                  </Box>
                                  {(cached > 0 || cacheWrite > 0) && (
                                    <>
                                      <Box>
                                        <Typography sx={{ fontSize: '0.65rem', color: textM, mb: 0.25 }}>NON-CACHED</Typography>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#22D3EE' }}>{fmt(nonCached)}</Typography>
                                      </Box>
                                      {cached > 0 && (
                                        <Box>
                                          <Typography sx={{ fontSize: '0.65rem', color: textM, mb: 0.25 }}>CACHED</Typography>
                                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#34D399' }}>{fmt(cached)}</Typography>
                                        </Box>
                                      )}
                                      {cacheWrite > 0 && (
                                        <Box>
                                          <Typography sx={{ fontSize: '0.65rem', color: textM, mb: 0.25 }}>CACHE WRITE</Typography>
                                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#F59E0B' }}>{fmt(cacheWrite)}</Typography>
                                        </Box>
                                      )}
                                    </>
                                  )}
                                  <Box>
                                    <Typography sx={{ fontSize: '0.65rem', color: textM, mb: 0.25 }}>OUTPUT</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#A855F7' }}>
                                      {fmt(mu.output_tokens ?? 0)}
                                      {modelLimit && (modelLimit as ModelLimit).output_tokens != null && <Box component="span" sx={{ color: textM, fontWeight: 400, ml: 0.5 }}>/ {fmt((modelLimit as ModelLimit).output_tokens as number)}</Box>}
                                    </Typography>
                                  </Box>
                                  {cost && (
                                    <Box>
                                      <Typography sx={{ fontSize: '0.65rem', color: textM, mb: 0.25 }}>EST. COST</Typography>
                                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: textP }}>
                                        ${cost.total_cost.toFixed(4)}
                                        {cost.cache_savings > 0 && (
                                          <Box component="span" sx={{ color: '#34D399', fontWeight: 400, ml: 0.5 }}>
                                            (saved ${cost.cache_savings.toFixed(4)} via cache)
                                          </Box>
                                        )}
                                        {cost.cache_write_cost > 0 && (
                                          <Box component="span" sx={{ color: '#F59E0B', fontWeight: 400, ml: 0.5 }}>
                                            (+${cost.cache_write_cost.toFixed(4)} cache write)
                                          </Box>
                                        )}
                                      </Typography>
                                      {cost.short_context_cost > 0 && cost.long_context_cost > 0 && (
                                        <Typography sx={{ fontSize: '0.62rem', color: textM, mt: 0.25 }}>
                                          ${cost.short_context_cost.toFixed(4)} standard + ${cost.long_context_cost.toFixed(4)} long-context
                                        </Typography>
                                      )}
                                      {cost.short_context_cost === 0 && cost.long_context_cost > 0 && (
                                        <Typography sx={{ fontSize: '0.62rem', color: '#F59E0B', mt: 0.25 }}>long-context rate</Typography>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Lifetime usage */}
              {(lifetimeLoading || lifetimeUsage) && (
                <Box>
                  <Typography sx={{ ...sectionLabel, mb: 1 }}>Lifetime Usage</Typography>
                  {lifetimeLoading ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {[1, 2, 3].map(i => <Skeleton key={i} width={100} height={48} sx={{ borderRadius: '6px', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }} />)}
                    </Box>
                  ) : lifetimeUsage && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {[
                        { label: 'Total Input', value: lifetimeUsage.total_usage?.input_tokens, color: '#22D3EE' },
                        { label: 'Total Output', value: lifetimeUsage.total_usage?.output_tokens, color: '#A855F7' },
                        { label: 'Total Requests', value: lifetimeUsage.total_requests, color: textS },
                      ].filter(s => s.value !== undefined).map(({ label, value, color }) => {
                        const fmt = (n: number) => n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M' : n >= 1_000 ? (n / 1_000).toFixed(0) + 'K' : n?.toString() ?? '—';
                        return (
                          <Box key={label} sx={{ px: 2, py: 1.5, bgcolor: subBg, borderRadius: '6px', border: `1px solid ${border}`, minWidth: 110 }}>
                            <Typography sx={{ fontSize: '0.65rem', color: textM, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>{label}</Typography>
                            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color, letterSpacing: '-0.03em' }}>{fmt(value)}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {/* Viewer request actions */}
              {(user?.role === 'viewer' || user?.role === 'app_viewer') && (
                <Box>
                  <Typography sx={{ ...sectionLabel, mb: 1 }}>Requests</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" onClick={() => { setReqTokenOpen(true); setReqReason(''); setReqInputLimit(''); setReqOutputLimit(''); }}
                      sx={{ fontSize: '0.75rem', borderColor: border, color: textS }}>
                      Request Token Limit Increase
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => { setReqModelOpen(true); setReqReason(''); setReqModels(''); }}
                      sx={{ fontSize: '0.75rem', borderColor: border, color: textS }}>
                      Request Model Access
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Endpoint permissions */}
              <Box>
                <Typography sx={{ ...sectionLabel, mb: 1 }}>Endpoint Permissions</Typography>
                {selectedApp.permissions && Object.keys(selectedApp.permissions).length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {Object.entries(selectedApp.permissions).map(([endpoint, perm]) => (
                      <Box key={endpoint} sx={{ border: `1px solid ${border}`, borderRadius: '6px', overflow: 'hidden' }}>
                        <Box sx={{ px: 1.5, py: 1, bgcolor: subBg, borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <code style={{ fontSize: '0.8rem', fontWeight: 600, color: textP, background: 'none', padding: 0 }}>/{endpoint}</code>
                          <Typography sx={{ fontSize: '0.7rem', color: textM }}>
                            {perm.allowed_models.includes('*') ? 'All models' : `${perm.allowed_models.length} model${perm.allowed_models.length !== 1 ? 's' : ''}`}
                          </Typography>
                        </Box>
                        <Box sx={{ px: 1.5, py: 1 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: perm.model_limits && Object.keys(perm.model_limits).length > 0 ? 1 : 0 }}>
                            {perm.allowed_models.map((model: string) => (
                              <Chip key={model} label={model === '*' ? '* (all)' : model} size="small"
                                sx={{ bgcolor: model === '*' ? (isDark ? 'rgba(255,26,80,0.15)' : '#fee2e2') : (isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9'), color: model === '*' ? '#FF1A50' : textS, fontSize: '0.7rem', height: 20, border: 'none', fontWeight: model === '*' ? 600 : 400 }} />
                            ))}
                          </Box>
                          {perm.model_limits && Object.keys(perm.model_limits).length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {Object.entries(perm.model_limits as Record<string, ModelLimit>).map(([m, lim]) => (
                                <Box key={m} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                  <Typography sx={{ fontSize: '0.72rem', color: textS, minWidth: 140 }}>{m}</Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: textM }}>
                                    IN <Box component="span" sx={{ color: '#22D3EE', fontWeight: 600, ml: 0.5 }}>{fmtModelCap(lim.input_tokens, v => `${(v / 1000).toFixed(0)}K`)}</Box>
                                    {'  '}OUT <Box component="span" sx={{ color: '#A855F7', fontWeight: 600, ml: 0.5 }}>{fmtModelCap(lim.output_tokens, v => `${(v / 1000).toFixed(0)}K`)}</Box>
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ p: 2, bgcolor: subBg, borderRadius: '6px', border: `1px solid ${border}` }}>
                    <Typography sx={{ fontSize: '0.875rem', color: textM }}>v1 schema — flat model list</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {selectedApp.allowed_models.map((m) => (
                        <Chip key={m} label={m} size="small" sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9', color: textS, fontSize: '0.7rem', height: 20, border: 'none' }} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Viewers */}
              {user?.permissions?.can_create_apps && (
                <>
                  <Divider />
                  <Box>
                    <Typography sx={{ ...sectionLabel, mb: 1 }}>App Viewers ({(selectedApp.viewers ?? []).length}/5)</Typography>
                    {(selectedApp.viewers ?? []).length === 0 ? (
                      <Typography sx={{ fontSize: '0.8rem', color: textM }}>No viewers. Use edit to add viewers.</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {(selectedApp.viewers ?? []).map((email) => (
                          <Chip key={email} label={email} size="small"
                            avatar={<Avatar sx={{ bgcolor: '#3B82F6', fontSize: '0.6rem' }}>{email[0].toUpperCase()}</Avatar>}
                            sx={{ bgcolor: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff', color: isDark ? '#93C5FD' : '#1d4ed8', fontSize: '0.75rem', border: isDark ? '1px solid rgba(59,130,246,0.25)' : '1px solid #bfdbfe' }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {/* ── Activity Feed ─────────────────────────────────────────── */}
              <Divider />
              <Box>
                <Typography sx={{ ...sectionLabel, mb: 1.5 }}>Activity</Typography>

                {activityLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[0, 1, 2].map(i => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: isDark ? 'rgba(255,255,255,0.07)' : '#F1F5F9', flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ height: 12, bgcolor: isDark ? 'rgba(255,255,255,0.07)' : '#F1F5F9', borderRadius: 1, mb: 0.75, width: '60%' }} />
                          <Box sx={{ height: 10, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#F8FAFC', borderRadius: 1, width: '40%' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : appActivity.length === 0 ? (
                  <Typography sx={{ fontSize: '0.8rem', color: textM }}>No activity recorded for this app yet.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {appActivity.map((log, idx) => {
                      const isLast = idx === appActivity.length - 1;
                      const roleColor = log.admin_role === 'super_admin' ? '#FF1A50' : log.admin_role === 'admin' ? '#3B82F6' : '#8B5CF6';
                      const initials = log.admin_name
                        ? log.admin_name.split(' ').map((p: string) => p[0]).join('').toUpperCase().slice(0, 2)
                        : (log.admin_email || '?')[0].toUpperCase();

                      const actionLabel = (() => {
                        const d = log.details || {};
                        switch (log.action) {
                          case 'ADD_MODEL': return `added model ${(d.added_models || []).join(', ')}`;
                          case 'REVOKE_MODEL': return `removed model ${d.revoked_model || ''}`;
                          case 'GRANT_ENDPOINT': return `granted endpoint ${d.endpoint || ''}`;
                          case 'REVOKE_ENDPOINT': return `revoked endpoint ${d.endpoint || ''}`;
                          case 'ADD_APP_VIEWER': return `added viewer ${d.viewer_email || ''}`;
                          case 'REMOVE_APP_VIEWER': return `removed viewer ${d.viewer_email || ''}`;
                          case 'REGISTER_APP': return 'registered this app';
                          case 'DELETE_APP': return 'deleted this app';
                          case 'UPDATE_APP': {
                            const FIELD_LABELS: Record<string, string> = {
                              token_limit_input:  'input token limit',
                              token_limit_output: 'output token limit',
                              is_active:          'status',
                              department:         'department',
                              project_head:       'project head',
                            };
                            const afterVals  = (d.after  || {}) as Record<string, any>;
                            const beforeVals = (d.before || {}) as Record<string, any>;
                            const fmt = (v: any) => typeof v === 'number' ? v.toLocaleString() : String(v === true ? 'active' : v === false ? 'inactive' : v);
                            const parts: string[] = [];

                            // Scalar field changes (token limits, status, etc.)
                            const PERM_KEYS = new Set(['endpoints_added','endpoints_removed','model_changes','limit_changes']);
                            const scalarFields = Object.keys(afterVals).filter(k => !PERM_KEYS.has(k));
                            scalarFields.forEach(k => {
                              const label = FIELD_LABELS[k] || k.replace(/_/g, ' ');
                              const bv = beforeVals[k];
                              const av = afterVals[k];
                              parts.push(bv !== undefined ? `${label}: ${fmt(bv)} → ${fmt(av)}` : label);
                            });

                            // Endpoint / model permission changes — these have their own verb so don't prefix with "changed"
                            const added    = (afterVals.endpoints_added   || []) as string[];
                            const removed  = (afterVals.endpoints_removed || []) as string[];
                            const mchanges = (afterVals.model_changes  || {}) as Record<string, { added: string[]; removed: string[] }>;
                            const lchanges = (afterVals.limit_changes  || {}) as Record<string, Record<string, { before: any; after: any }>>;
                            const permParts: string[] = [];
                            if (added.length)   permParts.push(`enabled ${added.join(', ')}`);
                            if (removed.length) permParts.push(`disabled ${removed.join(', ')}`);
                            Object.entries(mchanges).forEach(([ep, mc]) => {
                              if (mc.added.length)   permParts.push(`added ${mc.added.join(', ')} to ${ep}`);
                              if (mc.removed.length) permParts.push(`removed ${mc.removed.join(', ')} from ${ep}`);
                            });
                            Object.entries(lchanges).forEach(([ep, models]) => {
                              Object.entries(models).forEach(([model, diff]) => {
                                const fmtLim = (v: any) => v && typeof v === 'object'
                                  ? `${Number(v.input_tokens).toLocaleString()} / ${Number(v.output_tokens).toLocaleString()}`
                                  : '—';
                                permParts.push(`updated ${model} token limits on ${ep}: ${fmtLim(diff.before)} → ${fmtLim(diff.after)}`);
                              });
                            });

                            // Compose: scalar changes get "changed" prefix; permission actions stand alone
                            if (parts.length > 0 && permParts.length > 0) return `changed ${parts.join(' · ')} · ${permParts.join(' · ')}`;
                            if (parts.length > 0)    return `changed ${parts.join(' · ')}`;
                            if (permParts.length > 0) return permParts.join(' · ');
                            return 'updated app settings';
                          }
                          case 'UPDATE_APP_KEYS': return `updated ${(d.providers_updated || []).join(', ')} key(s)`;
                          case 'UPDATE_APP_KEY': return 'updated OpenAI API key';
                          case 'ADD_MODEL_TO_ENDPOINT': return `added ${d.model} to ${d.endpoint}`;
                          case 'REMOVE_MODEL_FROM_ENDPOINT': return `removed ${d.model} from ${d.endpoint}`;
                          case 'UPDATE_MODEL_LIMITS': return `updated limits for ${(d.updated_models || []).join(', ')}`;
                          case 'UPDATE_ENDPOINT_MODEL_LIMITS': return `updated limits for ${d.model} on ${d.endpoint}`;
                          case 'KEYS_APP': return 'viewed API keys';
                          case 'LIFETIME_USAGE': return 'viewed usage history';
                          case 'RETRY_MODEL_CHECK': return 'retried model availability check';
                          case 'SUBMIT_TOKEN_REQUEST': return `requested token limit increase`;
                          case 'SUBMIT_MODEL_REQUEST': return `requested model access for ${(d.requested_models || []).join(', ')}`;
                          case 'APPROVE_REQUEST': return d.type === 'token_limit_increase'
                            ? 'approved token limit increase'
                            : d.type === 'model_access'
                            ? `approved model access for ${(d.approved_models || []).join(', ')}`
                            : 'approved request';
                          case 'REJECT_REQUEST': return `rejected ${(d.type || 'request').replace(/_/g, ' ')}`;
                          case 'INITIATE_DELETE_APP': return 'initiated app deletion (awaiting 2nd approval)';
                          case 'CONFIRM_DELETE_APP': return 'confirmed app deletion';
                          case 'CANCEL_DELETE_APP': return 'cancelled pending deletion';
                          case 'INITIATE_RENAME_APP': {
                            const to = d?.after?.app_name;
                            return to
                              ? `initiated rename to "${to}" (awaiting 2nd approval)`
                              : 'initiated app rename (awaiting 2nd approval)';
                          }
                          case 'CONFIRM_RENAME_APP': {
                            const from = d?.before?.app_name;
                            const to = d?.after?.app_name;
                            return from && to
                              ? `renamed app from "${from}" to "${to}"`
                              : 'confirmed app rename';
                          }
                          case 'CANCEL_RENAME_APP': return 'cancelled pending rename';
                          case 'ASSISTANT_QUERY': return 'asked the assistant a question';
                          case 'ASSISTANT_TOOL_CALL': {
                            const tool = d?.tool;
                            return tool ? `assistant used ${tool}` : 'assistant used a tool';
                          }
                          default: return log.action.toLowerCase().replace(/_/g, ' ');
                        }
                      })();

                      const ts = new Date(log.timestamp);
                      const relativeTime = (() => {
                        const diff = Date.now() - ts.getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 1) return 'just now';
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        const days = Math.floor(hrs / 24);
                        if (days < 30) return `${days}d ago`;
                        return ts.toLocaleDateString();
                      })();

                      const hasDiff = log.details?.before && log.details?.after;

                      return (
                        <Box key={log._id || idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          {/* Timeline */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                            <Box sx={{
                              width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: `${roleColor}1A`, color: roleColor, fontSize: '0.62rem', fontWeight: 700,
                              border: `1.5px solid ${roleColor}40`,
                            }}>
                              {initials}
                            </Box>
                            {!isLast && <Box sx={{ width: 1, flex: 1, minHeight: 12, bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0', my: 0.25 }} />}
                          </Box>

                          {/* Content */}
                          <Box sx={{ flex: 1, pb: isLast ? 0 : 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: textP }}>
                                {log.admin_name || log.admin_email}
                              </Typography>
                              <Typography sx={{ fontSize: '0.82rem', color: textS }}>{actionLabel}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                              <Typography sx={{ fontSize: '0.7rem', color: textM }}>{relativeTime}</Typography>
                              <Typography sx={{ fontSize: '0.65rem', color: textM }}>·</Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: textM }}>{ts.toLocaleString()}</Typography>
                            </Box>

                            {/* Before → After diff inline */}
                            {hasDiff && (() => {
                              const fields = Object.keys(log.details!.after);
                              return (
                                <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {fields.map(f => (
                                    <Box key={f} sx={{
                                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                      bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC',
                                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
                                      borderRadius: '5px', px: 1, py: 0.25, fontSize: '0.7rem',
                                    }}>
                                      <Typography component="span" sx={{ fontSize: '0.68rem', color: textM, fontWeight: 600 }}>{f}: </Typography>
                                      <Typography component="span" sx={{ fontSize: '0.68rem', color: isDark ? '#FCA5A5' : '#B91C1C', textDecoration: 'line-through' }}>
                                        {String(log.details!.before[f] ?? '—')}
                                      </Typography>
                                      <Typography component="span" sx={{ fontSize: '0.65rem', color: textM }}>→</Typography>
                                      <Typography component="span" sx={{ fontSize: '0.68rem', color: isDark ? '#6EE7B7' : '#059669', fontWeight: 600 }}>
                                        {String(log.details!.after[f] ?? '—')}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              );
                            })()}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDetailsDialog} variant="outlined" sx={{ borderColor: border, color: textS }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════════
          Confirm Fetch Models Dialog
      ════════════════════════════════════════════════════════════════════════ */}
      <Dialog open={confirmFetchOpen} onClose={() => setConfirmFetchOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${border}`, pb: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: textP }}>Verify API Keys</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: textM, fontWeight: 400, mt: 0.25 }}>
            Confirm your API keys before fetching models
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {formData.openai_api_key && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: textM, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    OpenAI API Key
                  </Typography>
                  <Chip 
                    label="OpenAI" 
                    size="small" 
                    sx={{ 
                      bgcolor: isDark ? 'rgba(16,185,129,0.12)' : '#d1fae5',
                      color: '#10B981',
                      fontWeight: 600, 
                      fontSize: '0.6rem', 
                      height: 16,
                      border: 'none',
                      '& .MuiChip-label': { px: 0.75 }
                    }} 
                  />
                </Box>
                <Box sx={{
                  p: 1.5, bgcolor: subBg, border: `1px solid ${border}`, borderRadius: '6px',
                  fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.85rem', color: textP, wordBreak: 'break-all'
                }}>
                  {formData.openai_api_key.substring(0, 7)}…{formData.openai_api_key.substring(formData.openai_api_key.length - 4)}
                </Box>
              </Box>
            )}
            {formData.anthropic_api_key && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: textM, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Anthropic API Key
                  </Typography>
                  <Chip 
                    label="Anthropic" 
                    size="small" 
                    sx={{ 
                      bgcolor: isDark ? 'rgba(168,85,247,0.12)' : '#f3e8ff',
                      color: '#A855F7',
                      fontWeight: 600, 
                      fontSize: '0.6rem', 
                      height: 16,
                      border: 'none',
                      '& .MuiChip-label': { px: 0.75 }
                    }} 
                  />
                </Box>
                <Box sx={{
                  p: 1.5, bgcolor: subBg, border: `1px solid ${border}`, borderRadius: '6px',
                  fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.85rem', color: textP, wordBreak: 'break-all'
                }}>
                  {formData.anthropic_api_key.substring(0, 10)}…{formData.anthropic_api_key.substring(formData.anthropic_api_key.length - 4)}
                </Box>
              </Box>
            )}
            <Alert severity="info" sx={{ fontSize: '0.8rem', py: 0.5 }}>
              {formData.openai_api_key && formData.anthropic_api_key 
                ? 'These keys will be used to fetch available models from OpenAI and Anthropic.'
                : formData.openai_api_key 
                  ? 'This key will be used to fetch available models from OpenAI.'
                  : 'This key will be used to fetch available Claude models from Anthropic.'}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmFetchOpen(false)} disabled={fetchingModels} sx={{ color: textS }}>Cancel</Button>
          <Button onClick={handleConfirmFetchModels} variant="contained" disabled={fetchingModels}
            startIcon={fetchingModels ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}>
            {fetchingModels ? 'Fetching…' : 'Confirm & Fetch Models'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auth Key reveal dialog */}
      <Dialog open={!!newAuthKey} onClose={() => setNewAuthKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${border}`, pb: 1.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: textP }}>Application Registered</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#F59E0B', fontWeight: 500, mt: 0.5 }}>
            Save this auth key now — it will not be shown again.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: textM, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Auth Key</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC', border: `1px solid ${border}`, borderRadius: '6px', px: 1.5, py: 1 }}>
            <Typography sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.8rem', color: textP, flex: 1, wordBreak: 'break-all', letterSpacing: showAuthKey ? '0.02em' : '0.2em' }}>
              {showAuthKey ? newAuthKey : '•'.repeat(Math.min(newAuthKey?.length ?? 0, 48))}
            </Typography>
            <IconButton size="small" onClick={() => setShowAuthKey(v => !v)} sx={{ color: textS, '&:hover': { color: textP } }}>
              {showAuthKey ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
            </IconButton>
            <IconButton size="small" onClick={() => { copyToClipboard(newAuthKey!, 'Auth Key'); }} sx={{ color: textS, '&:hover': { color: textP } }}>
              <ContentCopy sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: `1px solid ${border}` }}>
          <Button onClick={() => setNewAuthKey(null)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>

      {/* ── Request: Token Limit Increase ───────────────────────────────── */}
      <Dialog open={reqTokenOpen} onClose={() => setReqTokenOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>Request Token Limit Increase</DialogTitle>
        <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Requested Input Limit" type="number" size="small" fullWidth value={reqInputLimit} onChange={e => setReqInputLimit(e.target.value)} placeholder={selectedApp?.token_limit_input?.toLocaleString()} />
          <TextField label="Requested Output Limit" type="number" size="small" fullWidth value={reqOutputLimit} onChange={e => setReqOutputLimit(e.target.value)} placeholder={selectedApp?.token_limit_output?.toLocaleString()} />
          <TextField label="Reason" multiline rows={3} size="small" fullWidth value={reqReason} onChange={e => setReqReason(e.target.value)} placeholder="Why do you need more tokens?" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReqTokenOpen(false)} size="small">Cancel</Button>
          <Button variant="contained" size="small" disabled={submittingReq || !reqInputLimit || !reqOutputLimit}
            onClick={async () => {
              if (!selectedApp) return;
              setSubmittingReq(true);
              try {
                await apiService.submitTokenLimitRequest(selectedApp.app_id, parseInt(reqInputLimit), parseInt(reqOutputLimit), reqReason || undefined);
                showSnack('Token limit request submitted', 'success');
                setReqTokenOpen(false);
              } catch (e: any) { showSnack(e.response?.data?.detail || 'Failed to submit request', 'error'); }
              finally { setSubmittingReq(false); }
            }}>
            {submittingReq ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Request: Model Access ────────────────────────────────────────── */}
      <Dialog open={reqModelOpen} onClose={() => setReqModelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>Request Model Access</DialogTitle>
        <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Models (comma-separated)" size="small" fullWidth value={reqModels} onChange={e => setReqModels(e.target.value)} placeholder="gpt-4o, gpt-4o-mini" />
          <TextField label="Reason" multiline rows={3} size="small" fullWidth value={reqReason} onChange={e => setReqReason(e.target.value)} placeholder="Why do you need access to these models?" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReqModelOpen(false)} size="small">Cancel</Button>
          <Button variant="contained" size="small" disabled={submittingReq || !reqModels.trim()}
            onClick={async () => {
              if (!selectedApp) return;
              setSubmittingReq(true);
              try {
                const models = reqModels.split(',').map(m => m.trim()).filter(Boolean);
                await apiService.submitModelAccessRequest(selectedApp.app_id, models, reqReason || undefined);
                showSnack('Model access request submitted', 'success');
                setReqModelOpen(false);
              } catch (e: any) { showSnack(e.response?.data?.detail || 'Failed to submit request', 'error'); }
              finally { setSubmittingReq(false); }
            }}>
            {submittingReq ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════
          Rename App Dialog (super admin, 2-approval)
        ═══════════════════════════════════════════════════════════════════ */}
      <Dialog open={openRename} onClose={closeRenameDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>Rename application</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, mt: 1, mb: 2, bgcolor: isDark ? 'rgba(59,130,246,0.1)' : '#eff6ff', border: '1px solid', borderColor: isDark ? 'rgba(59,130,246,0.35)' : '#93c5fd', borderRadius: '8px' }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3B82F6', mb: 0.5 }}>
              Traffic will be frozen
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#93C5FD' : '#1E40AF' }}>
              All API traffic to <strong>{renameTarget?.app_name}</strong> will start returning <strong>423 Locked</strong> the moment you initiate this rename, and stays frozen until a second super admin approves or cancels the request.
            </Typography>
          </Box>
          <TextField
            label="New application name"
            fullWidth
            autoFocus
            margin="dense"
            value={renameNewName}
            onChange={(e) => setRenameNewName(e.target.value)}
            disabled={renaming}
            helperText="Existing API keys and integrations keep working — only the display name changes."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenameDialog} disabled={renaming}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitRename}
            disabled={renaming || !renameNewName.trim() || renameNewName.trim() === renameTarget?.app_name}
            sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' } }}
          >
            {renaming ? 'Initiating…' : 'Initiate rename'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} variant="filled" sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AppsPage;
